import './App.css';
// Version 1.0.1 - Build: 2024-11-14 - Removed delete button from iframe overlay
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, useParams, useSearchParams, Navigate, Link } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import Auth from './Auth';
import CreateAccount from './Login';
import ConnectGoogle from './ConnectGoogle';
import { Privacy, Terms, Report } from './LegalPages';
import axios from 'axios';
import { FaThumbsUp, FaThumbsDown, FaPlus, FaSearch, FaTimes, FaTrashAlt, FaRedo, FaUser, FaStar, FaCalendarAlt, FaSteamSymbol, FaYoutube } from 'react-icons/fa';
import VideoSubmissionModal, { VIDEO_SUBMITTED_EVENT } from './VideoSubmissionModal';
import LiveWorld from './LiveWorld';
import { parseTimeParam, rememberVideoTitle } from './videoMoment';
import { CLIP_CREATED_EVENT, CLIP_DELETED_EVENT, rememberClipTitle, clipsFeedWithLinkedFirst, buildClipPath, isClipInsideYoutubeArchive } from './clipShare';
import ClipsPage from './ClipsPage';
import HallsAdmin from './admin/HallsAdmin';
import GuestXpPrompt from './GuestXpPrompt';
import GoogleAnalytics from './GoogleAnalytics';
import { fetchAccountXp, mergeGuestXpToAccount, publishXp, XP_EVENT } from './xp';
import { ProfileClipList, ProfileMediaSwitch } from './ProfileClips';

/** Prefer new schema flags; fall back to legacy description status strings. */
const videoIsFeatured = (video) => {
  if (!video) return false;
  if (typeof video.isFeatured === 'boolean') return video.isFeatured;
  return video.description === 'Featured';
};

const videoIsArchived = (video) => {
  if (!video) return false;
  if (typeof video.isArchived === 'boolean') return video.isArchived;
  return !!(video.description && video.description.startsWith('Archived by'));
};

const pad2 = (n) => String(n).padStart(2, '0');

/** Local calendar date as YYYY-MM-DD */
const formatLocalDate = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const formatFeedDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const today = formatLocalDate();
  if (dateStr === today) return 'Today';
  const [y, m, day] = dateStr.split('-').map(Number);
  const d = new Date(y, m - 1, day);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Local-day start/end as ISO strings for feed queries */
const localDayBounds = (dateStr) => {
  const raw = dateStr || formatLocalDate();
  const [y, m, day] = raw.split('-').map(Number);
  const start = new Date(y, m - 1, day, 0, 0, 0, 0);
  const end = new Date(y, m - 1, day, 23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
};

/** Status chip for the current video: Live / Today / Jul 12 (+ local date for calendar) */
const statusForVideo = (video) => {
  if (!video) return { label: '', date: '' };
  if (video.liveStatus === 'live') {
    return { label: 'Live', date: formatLocalDate() };
  }
  if (!video.createdAt) return { label: '', date: '' };
  const date = formatLocalDate(new Date(video.createdAt));
  return { label: formatFeedDateLabel(date), date };
};

// Utility function to format time since posted
const formatTimeAgo = (dateString) => {
  const now = new Date();
  const postDate = new Date(dateString);
  const diffMs = now - postDate;

  // Convert time differences to minutes, hours, days
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // For times less than 60 minutes ago
  if (diffMinutes < 60) {
    return `${diffMinutes}m`;
  }

  // For times less than 24 hours ago
  if (diffHours < 24) {
    return `${diffHours}h`;
  }

  // For times less than 30 days ago
  if (diffDays < 30) {
    return `${diffDays}d`;
  }

  // For times more than a month ago
  return postDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const feedPlayUrl = (url) => String(url || '').split('#')[0];

const feedYoutubeId = (url) => {
  const play = feedPlayUrl(url);
  if (!play) return '';
  return play.split('/').pop().split('?')[0] || '';
};

/**
 * Profile-page button that shows either "Create my Hall" (POSTs to
 * /api/halls/user and navigates to /@username) or "Go to my Hall" if it
 * already exists. Only visible on the signed-in user's own profile.
 */
const UserHallButton = ({ user, appUser, isCurrentUser }) => {
  const navigate = useNavigate();
  const [hall, setHall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user?.username) return undefined;
    let cancelled = false;
    setLoading(true);
    fetch(`/api/halls/${encodeURIComponent('@' + user.username)}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { if (!cancelled && data?.hall) setHall(data.hall); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.username]);

  if (!user?.username) return null;

  // Visitors always see "Visit Hall" if it exists; but the "create" flow is
  // only available to the signed-in user themselves.
  if (!isCurrentUser) {
    if (!hall) return null;
    return (
      <button
        onClick={() => navigate(`/@${user.username}`)}
        className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-blue-600 hover:bg-blue-700 text-white"
      >
        Visit @{user.username}
      </button>
    );
  }

  // Own profile
  if (loading) {
    return (
      <button disabled className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 text-gray-500 cursor-wait">
        Checking Hall…
      </button>
    );
  }
  if (hall) {
    return (
      <button
        onClick={() => navigate(`/@${user.username}`)}
        className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-blue-600 hover:bg-blue-700 text-white"
      >
        Go to my Hall
      </button>
    );
  }

  const create = async () => {
    setSubmitting(true);
    try {
      const res = await fetch('/api/halls/user', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok && res.status !== 409) {
        alert(data?.error || 'Failed to create hall');
        return;
      }
      navigate(`/@${user.username}`);
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <button
      onClick={create}
      disabled={submitting || !appUser}
      className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white"
      title="Create your personal Hall at /@username"
    >
      {submitting ? 'Creating…' : 'Create my Hall'}
    </button>
  );
};

const StripePayoutButton = ({ isCurrentUser }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isCurrentUser) return undefined;
    let cancelled = false;
    fetch('/api/stripe/connect/status', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) setStatus(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isCurrentUser]);

  if (!isCurrentUser) return null;

  const start = async (path) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(path, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      alert(data.error || 'Stripe request failed');
    } catch (err) {
      alert(err.message || 'Stripe request failed');
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  if (status.canSell) {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => start('/api/stripe/connect/dashboard')}
        className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-60"
      >
        {busy ? 'Opening…' : 'Stripe payouts'}
      </button>
    );
  }
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => start('/api/stripe/connect/onboard')}
      className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-amber-500 hover:bg-amber-400 text-black disabled:opacity-60"
      title="Connect Stripe Express so VIP pass sales pay out to you"
    >
      {busy ? 'Opening…' : (status.connected ? 'Finish Stripe setup' : 'Connect Stripe')}
    </button>
  );
};

// User profile component
const UserProfile = ({ appUser, isAuthenticated }) => {
  const { username } = useParams();
  const location = useLocation();
  const isAppMode = !location.pathname.startsWith('/legacy');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserStatus, setCurrentUserStatus] = useState(null);
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [videoToArchive, setVideoToArchive] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [statusChangeError, setStatusChangeError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [initialLoadingComplete, setInitialLoadingComplete] = useState(false);
  // Add state for archived videos toggle
  const [showArchived, setShowArchived] = useState(false);
  const [mediaTab, setMediaTab] = useState('clips');
  const [clipToArchive, setClipToArchive] = useState(null);
  const [canViewArchived, setCanViewArchived] = useState(false);
  const [accountActionError, setAccountActionError] = useState('');
  const [accountActionBusy, setAccountActionBusy] = useState(false);

  // Reset showArchived state when username changes (navigating to different user profile)
  useEffect(() => {
    setShowArchived(false);
    setMediaTab('clips');
  }, [username]);

  useEffect(() => {
    // Set current user status from App component
    if (appUser) {
      setCurrentUserStatus(appUser.status);
    } else {
      setCurrentUserStatus(null);
    }

    // We now know whether authentication is checked
    setAuthChecked(true);
  }, [appUser, isAuthenticated]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // Start by setting loading states
        setLoading(true);
        setError(null);
        // Important: Reset loading complete state at the beginning
        setInitialLoadingComplete(false);
        // Also reset auth checked until we're done loading
        setAuthChecked(false);

        // Build query parameters
        const queryParams = new URLSearchParams();
        if (showArchived) {
          queryParams.append('showArchived', 'true');
        }
        const queryString = queryParams.toString();
        const url = `/api/users/${username}${queryString ? '?' + queryString : ''}`;

        const response = await fetch(url, {
          credentials: 'include'
        });

        if (response.status === 404) {
          setError('User not found');
          setLoading(false);
          // Set these after all data is loaded
          setInitialLoadingComplete(true);
          setAuthChecked(true);
          return;
        }

        if (!response.ok) {
          throw new Error('Error fetching user profile');
        }

        const data = await response.json();
        setUser(data);

        // Set the permission state from server response
        setCanViewArchived(data.canViewArchived || false);

        // Debug logging
        console.log('User profile loaded:', {
          username: data.username,
          canViewArchived: data.canViewArchived,
          videosCount: data.videos ? data.videos.length : 0,
          isAuthenticated,
          appUser: appUser ? appUser.username : 'none'
        });

        // Check if this is the current user's profile
        setIsCurrentUser(
          !!(appUser && appUser.username.toLowerCase() === username.toLowerCase())
        );

        setLoading(false);

        // Only set these after all data is loaded and processed
        setInitialLoadingComplete(true);
        setAuthChecked(true);
      } catch (error) {
        console.error('Error:', error);
        setError('Failed to load user profile. Please try again.');
        setLoading(false);
        // Set these even in error case to ensure UI is updated
        setInitialLoadingComplete(true);
        setAuthChecked(true);
      }
    };

    fetchUserProfile();
  }, [username, appUser, showArchived]); // Add showArchived to dependencies

  useEffect(() => {
    if (!isCurrentUser) return undefined;
    const onXp = (e) => {
      if (typeof e.detail?.xp !== 'number') return;
      setUser((prev) => (prev && prev.xp !== e.detail.xp ? { ...prev, xp: e.detail.xp } : prev));
    };
    window.addEventListener(XP_EVENT, onXp);
    fetchAccountXp();
    return () => window.removeEventListener(XP_EVENT, onXp);
  }, [isCurrentUser]);

  const handleArchiveVideo = async (videoId) => {
    try {
      const response = await fetch(`/api/videos/${videoId}/archive`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Update the user state to remove the archived video and its clips
        setUser(prevUser => ({
          ...prevUser,
          videos: prevUser.videos.filter(video => video.videoId !== videoId),
          clips: (prevUser.clips || []).map((clip) => (
            String(clip.videoId) === String(videoId)
              ? { ...clip, isArchived: true, archivedWithVideo: true }
              : clip
          )).filter((clip) => showArchived || !clip.isArchived)
        }));
        // Close the modal
        setShowArchiveModal(false);
        setVideoToArchive(null);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to archive video');
      }
    } catch (error) {
      console.error('Error archiving video:', error);
      alert('Failed to archive video. Please try again.');
    }
  };

  const handleArchiveClip = async (clipId) => {
    try {
      const response = await fetch(`/api/clips/${encodeURIComponent(clipId)}/archive`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        window.dispatchEvent(new CustomEvent(CLIP_DELETED_EVENT, { detail: { clipId } }));
        setUser((prevUser) => ({
          ...prevUser,
          clips: (prevUser.clips || []).map((clip) => (
            String(clip.clipId) === String(clipId)
              ? { ...clip, isArchived: true, archivedWithVideo: false }
              : clip
          )).filter((clip) => showArchived || !clip.isArchived)
        }));
        setClipToArchive(null);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to remove clip');
      }
    } catch (error) {
      console.error('Error archiving clip:', error);
      alert('Failed to remove clip. Please try again.');
    }
  };

  const handleUnarchiveClip = async (clip) => {
    try {
      const response = await fetch(`/api/clips/${encodeURIComponent(clip.clipId)}/unarchive`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        setUser((prevUser) => ({
          ...prevUser,
          clips: (prevUser.clips || []).map((item) => (
            String(item.clipId) === String(clip.clipId)
              ? { ...item, isArchived: false, archivedWithVideo: false }
              : item
          ))
        }));
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to unarchive clip');
      }
    } catch (error) {
      console.error('Error unarchiving clip:', error);
      alert('Failed to unarchive clip. Please try again.');
    }
  };

  const handleToggleFeatured = async (videoId) => {
    try {
      const response = await fetch(`/api/videos/${videoId}/toggle-featured`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update the user state to reflect the featured status change
        setUser(prevUser => ({
          ...prevUser,
          videos: prevUser.videos.map(video =>
            video.videoId === videoId
              ? {
                  ...video,
                  isFeatured: data.isFeatured,
                  description: data.isFeatured ? 'Featured' : null
                }
              : video
          )
        }));
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to toggle featured status');
      }
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert('Failed to toggle featured status. Please try again.');
    }
  };

  const handleChangeStatus = async () => {
    if (selectedStatus === undefined) return;

    setStatusChangeLoading(true);
    setStatusChangeError(null);

    try {
      const response = await fetch(`/api/admin/users/${username}/status`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: selectedStatus })
      });

      if (response.ok) {
        const data = await response.json();
        setUser(prev => ({ ...prev, status: data.status }));
        setTimeout(() => {
          setStatusModalOpen(false);
        }, 2000);
      } else {
        const errorData = await response.json();
        setStatusChangeError(errorData.error || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      setStatusChangeError('Failed to update user status. Please try again.');
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // Update canModifyStatus to check isAuthenticated
  const canModifyStatus = () => {
    // If user is not authenticated, they can't modify status
    if (!isAuthenticated || !appUser) return false;

    // Only admins/owners can modify status now (removing moderators from this check)
    if (!['Admin', 'Owner'].includes(currentUserStatus)) return false;

    // Check if the profile is the user's own profile by comparing usernames
    if (user.username.toLowerCase() === appUser.username.toLowerCase()) {
      return false; // Never allow modifying own status
    }

    // Owner can modify anyone's status except themselves (additional safety check)
    if (currentUserStatus === 'Owner') return true;

    // Admins can only modify regular users (not other admins or owners)
    if (currentUserStatus === 'Admin') {
      // Admins can manage regular users and moderators, but not other admins or owners
      return !user.status || (user.status !== 'Admin' && user.status !== 'Owner');
    }

    return false;
  };

  // Check if user can promote/demote to Admin status (only Owners can do this)
  const canModifyAdminStatus = () => {
    return isAuthenticated && appUser && currentUserStatus === 'Owner';
  };

  // Check if user can promote/demote to Moderator status (Owners and Admins can do this)
  const canModifyModeratorStatus = () => {
    return isAuthenticated && appUser && ['Owner', 'Admin'].includes(currentUserStatus);
  };

  // Check if user can ban/unban users (both Owners and Admins can do this)
  const canBanUsers = () => {
    return isAuthenticated && appUser && ['Owner', 'Admin'].includes(currentUserStatus);
  };

  // Function to check if user has admin capabilities for video deletion
  const hasAdminCapabilities = (videoUsername) => {
    // If user is not authenticated, they don't have admin capabilities
    if (!isAuthenticated || !appUser) return false;

    // If current user is the video submitter, allow deleting own videos
    if (appUser.username === videoUsername) return true;

    const currentUserStatus = appUser?.status || '';

    // Only admins, owners, and moderators can have admin capabilities
    if (!['Admin', 'Owner', 'Moderator'].includes(currentUserStatus)) return false;

    // Owner can delete any video
    if (currentUserStatus === 'Owner') return true;

    // For UserProfile component, we can check the profile user's status to determine permissions
    // This is the status of the user whose profile we're viewing
    const profileUserStatus = user?.status || '';

    // Admins can delete videos from moderators and regular users, but not from owners or other admins
    if (currentUserStatus === 'Admin') {
      return profileUserStatus !== 'Admin' && profileUserStatus !== 'Owner';
    }

    // Moderators can only delete videos from regular users (no special status)
    if (currentUserStatus === 'Moderator') {
      return !profileUserStatus || (profileUserStatus !== 'Moderator' &&
        profileUserStatus !== 'Admin' &&
        profileUserStatus !== 'Owner' &&
        profileUserStatus !== 'Banned');
    }

    return false;
  };

  // Function to toggle archived videos view
  const handleToggleArchived = () => {
    setShowArchived(!showArchived);
  };

  // Function to handle unarchiving a video
  const handleUnarchiveVideo = async (videoId) => {
    try {
      const response = await fetch(`/api/videos/${videoId}/unarchive`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh the user profile to get updated video list
        const queryParams = new URLSearchParams();
        if (showArchived) {
          queryParams.append('showArchived', 'true');
        }
        const queryString = queryParams.toString();
        const url = `/api/users/${username}${queryString ? '?' + queryString : ''}`;

        const refreshResponse = await fetch(url, {
          credentials: 'include'
        });

        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          setUser(data);
        }
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to unarchive video');
      }
    } catch (error) {
      console.error('Error unarchiving video:', error);
      alert('Failed to unarchive video. Please try again.');
    }
  };

  // Function to check if user can unarchive a specific video
  const canUnarchiveVideo = (video) => {
    if (!isAuthenticated || !appUser) return false;

    // Prefer archivedByUserId; fall back to legacy description strings
    if (video.archivedByUserId != null && video.archivedByUserId === appUser.userId) {
      return true;
    }
    if (video.description) {
      if (video.description.includes(`Archived by user: ${appUser.username}`)) {
        return true;
      }
      if (video.description.includes(`Archived by admin: ${appUser.username}`)) {
        return true;
      }
    }

    // Apply same permission hierarchy as archiving
    const currentUserStatus = appUser?.status || '';

    // If viewing own profile, can unarchive own videos
    if (isCurrentUser) return true;

    // Only admins, owners, and moderators can have unarchive capabilities for others
    if (!['Admin', 'Owner', 'Moderator'].includes(currentUserStatus)) return false;

    // Owner can unarchive any video
    if (currentUserStatus === 'Owner') return true;

    // Check the status of the user whose profile is being viewed
    const profileUserStatus = user?.status || '';

    // Admins can unarchive videos from moderators and regular users, but not from owners or other admins
    if (currentUserStatus === 'Admin') {
      return profileUserStatus !== 'Admin' && profileUserStatus !== 'Owner';
    }

    // Moderators can only unarchive videos from regular users (no special status)
    if (currentUserStatus === 'Moderator') {
      return !profileUserStatus || (profileUserStatus !== 'Moderator' &&
        profileUserStatus !== 'Admin' &&
        profileUserStatus !== 'Owner' &&
        profileUserStatus !== 'Banned');
    }

    return false;
  };

  // Add specific authentication check for status tags display
  const shouldShowStatusTag = (status) => {
    // Debug logging to help diagnose rendering issues
    const debugInfo = {
      status,
      isAuthenticated,
      loading,
      initialLoadingComplete,
      authChecked,
      currentUserStatus
    };
    console.debug('Status tag check:', debugInfo);

    // Safety check - exit early if status is undefined or null
    if (!status) return false;

    // Always show banned status regardless of authentication
    if (status === 'Banned') return true;

    // Critical check - NEVER show admin tags until fully authenticated
    // This prevents tags from showing during initial loading or partial auth state
    if (!isAuthenticated || loading) return false;

    // Additional safety checks - never show admin tags during any loading state
    if (!initialLoadingComplete || !authChecked) return false;

    // Only show Admin/Owner/Moderator tags if:
    // 1. Current user is authenticated
    // 2. Current user has Admin or Owner status
    // 3. The profile has an Admin/Owner/Moderator status
    // 4. All loading is complete and auth is checked
    return (
      isAuthenticated &&
      currentUserStatus &&
      ['Admin', 'Owner'].includes(currentUserStatus) &&
      ['Admin', 'Owner', 'Moderator'].includes(status)
    );
  };

  if (loading) return <div className="text-center mt-8">Loading profile...</div>;
  if (error) return <div className="text-center mt-8 text-red-500">{error}</div>;
  if (!user) return <div className="text-center mt-8">User not found</div>;

  return (
    <div className="container mx-auto px-4 py-6">
      <Helmet>
        <title>{user.username} - BIGPLAYS.GG</title>
        <meta property="og:title" content={`${user.username} - BIGPLAYS.GG`} />
        <meta property="og:description" content={`Check out ${user.username}'s Counter-Strike 2 highlights on BIGPLAYS.GG`} />
        <meta property="og:url" content={`https://bigplays.gg/user/${user.username}`} />
        {user.avatar && <meta property="og:image" content={user.avatar} />}
        <meta property="og:type" content="profile" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${user.username} - BIGPLAYS.GG`} />
        <meta name="twitter:description" content={`Check out ${user.username}'s Counter-Strike 2 highlights on BIGPLAYS.GG`} />
        {user.avatar && <meta name="twitter:image" content={user.avatar} />}
      </Helmet>

      <div className="bg-white text-gray-900 rounded-lg shadow-md p-4 sm:p-6">
        {/* Header section with Show Archived button */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 flex-grow">
            {user.avatar && (
              <img
                src={user.avatar}
                alt={`${user.username}'s avatar`}
                className="w-20 h-20 rounded-full border-4 border-blue-500"
              />
            )}
            <div className="text-center sm:text-left">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{user.username}</h1>
              <p className="text-yellow-600 font-semibold mb-2">
                {typeof user.xp === 'number' ? user.xp : 0} XP
              </p>
              {user.status && initialLoadingComplete && authChecked && shouldShowStatusTag(user.status) && (
                <div className="mb-2">
                  <span className={`inline-block px-2 py-1 rounded text-sm font-semibold ${user.status === 'Admin' ? 'bg-blue-100 text-blue-800' :
                    user.status === 'Owner' ? 'bg-purple-100 text-purple-800' :
                      user.status === 'Banned' ? 'bg-red-100 text-red-800' :
                        user.status === 'Moderator' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                    }`}>
                    {user.status}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <p className="text-gray-600">Member since: {new Date(user.createdAt).toLocaleDateString()}</p>
                {user.steamId64 && (
                  <a
                    href={`https://steamcommunity.com/profiles/${user.steamId64}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-[#1b2838] hover:text-[#66c0f4] transition-colors"
                    title="View Steam Profile"
                    aria-label="View Steam Profile"
                  >
                    <FaSteamSymbol className="h-6 w-6" />
                  </a>
                )}
                {user.youtubeChannelId && (
                  <a
                    href={`https://www.youtube.com/channel/${user.youtubeChannelId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-[#ff0000] hover:text-[#cc0000] transition-colors"
                    title="View YouTube Channel"
                    aria-label="View YouTube Channel"
                  >
                    <FaYoutube className="h-6 w-6" />
                  </a>
                )}
                {isCurrentUser && (user.youtubeLinked || user.youtubeChannelId) && (
                  <button
                    type="button"
                    disabled={accountActionBusy}
                    onClick={async () => {
                      setAccountActionError('');
                      setAccountActionBusy(true);
                      try {
                        const res = await fetch('/api/youtube/im-live', {
                          method: 'POST',
                          credentials: 'include'
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(data.error || 'You are not live');
                        const slug = String(data.hallSlug || `@${user.username}`).replace(/^\/+/, '');
                        window.location.assign(`/${slug}`);
                      } catch (err) {
                        setAccountActionError(err.message);
                      } finally {
                        setAccountActionBusy(false);
                      }
                    }}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold transition-colors !bg-red-600 !text-white hover:!bg-red-700"
                  >
                    I’m Live
                  </button>
                )}
              </div>
              {isCurrentUser && (
                <div className="mt-3 flex flex-wrap gap-2 justify-center sm:justify-start">
                  {!user.steamId64 ? (
                    <a
                      href={`/api/auth/steam?returnTo=${encodeURIComponent(window.location.pathname)}`}
                      className="px-3 py-1.5 rounded text-sm font-medium bg-gray-800 text-white hover:bg-gray-700"
                    >
                      Connect Steam
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled={accountActionBusy}
                      onClick={async () => {
                        setAccountActionError('');
                        setAccountActionBusy(true);
                        try {
                          const res = await fetch('/api/auth/steam/disconnect', {
                            method: 'POST',
                            credentials: 'include'
                          });
                          const data = await res.json().catch(() => ({}));
                          if (!res.ok) throw new Error(data.error || 'Failed to disconnect Steam');
                          setUser((prev) => (prev ? { ...prev, steamId64: null } : prev));
                        } catch (err) {
                          setAccountActionError(err.message);
                        } finally {
                          setAccountActionBusy(false);
                        }
                      }}
                      className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200"
                    >
                      Disconnect Steam
                    </button>
                  )}
                  {user.youtubeLinked || user.youtubeChannelId ? (
                    <button
                      type="button"
                      disabled={accountActionBusy}
                      onClick={async () => {
                        setAccountActionError('');
                        setAccountActionBusy(true);
                        try {
                          const res = await fetch('/api/auth/youtube/disconnect', {
                            method: 'POST',
                            credentials: 'include'
                          });
                          const data = await res.json().catch(() => ({}));
                          if (!res.ok) throw new Error(data.error || 'Failed to disconnect YouTube');
                          setUser((prev) => (prev ? { ...prev, youtubeLinked: false, youtubeChannelId: null } : prev));
                        } catch (err) {
                          setAccountActionError(err.message);
                        } finally {
                          setAccountActionBusy(false);
                        }
                      }}
                      className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200"
                    >
                      Disconnect YouTube
                    </button>
                  ) : (
                    <a
                      href={`/api/auth/google/youtube?returnTo=${encodeURIComponent(window.location.pathname)}`}
                      className="px-4 py-2 rounded-md text-sm font-medium transition-colors !bg-red-600 !text-white hover:!bg-red-700"
                    >
                      Connect YouTube
                    </a>
                  )}
                </div>
              )}
              {isCurrentUser && accountActionError && (
                <p className="text-red-600 text-sm mt-2">{accountActionError}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-2 flex-shrink-0">
            <UserHallButton user={user} appUser={appUser} isCurrentUser={isCurrentUser} />
            <StripePayoutButton isCurrentUser={isCurrentUser} />
            {/* Show Archived button - only visible if user has permission */}
            {canViewArchived && (
              <button
                onClick={handleToggleArchived}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${showArchived
                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                title={showArchived ? 'Hide archived videos and clips' : 'Show archived videos and clips'}
              >
                {showArchived ? 'Hide Archived' : 'Show Archived'}
              </button>
            )}
          </div>
        </div>

        {/* Admin actions - only shown to users who can modify status */}
        {canModifyStatus() && (
          <div className="mt-4 border-t pt-4 border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Admin Actions</h3>
            <div className="flex flex-wrap gap-2">
              {/* Admin status buttons - only visible to Owners */}
              {canModifyAdminStatus() && user.status !== 'Admin' && user.status !== 'Owner' && (
                <button
                  onClick={() => {
                    setSelectedStatus("Admin");
                    setStatusModalOpen(true);
                  }}
                  className="px-3 py-1.5 rounded text-white text-sm font-medium bg-blue-600 hover:bg-blue-700"
                >
                  Make Admin
                </button>
              )}
              {canModifyAdminStatus() && user.status === 'Admin' && (
                <button
                  onClick={() => {
                    setSelectedStatus("");
                    setStatusModalOpen(true);
                  }}
                  className="px-3 py-1.5 rounded text-white text-sm font-medium bg-gray-600 hover:bg-gray-700"
                >
                  Remove Admin
                </button>
              )}

              {/* Moderator status buttons - visible to Owners and Admins */}
              {canModifyModeratorStatus() && user.status !== 'Moderator' && user.status !== 'Owner' &&
                /* Admins shouldn't be able to demote other Admins to Moderator */
                !(currentUserStatus === 'Admin' && user.status === 'Admin') && (
                  <button
                    onClick={() => {
                      setSelectedStatus("Moderator");
                      setStatusModalOpen(true);
                    }}
                    className="px-3 py-1.5 rounded text-white text-sm font-medium bg-green-600 hover:bg-green-700"
                  >
                    Make Moderator
                  </button>
                )}
              {canModifyModeratorStatus() && user.status === 'Moderator' && (
                <button
                  onClick={() => {
                    setSelectedStatus("");
                    setStatusModalOpen(true);
                  }}
                  className="px-3 py-1.5 rounded text-white text-sm font-medium bg-gray-600 hover:bg-gray-700"
                >
                  Remove Moderator
                </button>
              )}

              {/* Ban/Unban buttons - visible to Owners and Admins */}
              {canBanUsers() && user.status !== 'Banned' && user.status !== 'Owner' &&
                /* Admins shouldn't be able to ban other Admins */
                !(currentUserStatus === 'Admin' && user.status === 'Admin') ? (
                <button
                  onClick={() => {
                    setSelectedStatus("Banned");
                    setStatusModalOpen(true);
                  }}
                  className="px-3 py-1.5 rounded text-white text-sm font-medium bg-red-600 hover:bg-red-700"
                >
                  Ban User
                </button>
              ) : (
                canBanUsers() && user.status === 'Banned' && (
                  <button
                    onClick={() => {
                      setSelectedStatus("");
                      setStatusModalOpen(true);
                    }}
                    className="px-3 py-1.5 rounded text-white text-sm font-medium bg-green-600 hover:bg-green-700"
                  >
                    Unban User
                  </button>
                )
              )}
            </div>
          </div>
        )}

        <div className="mt-6">
          <ProfileMediaSwitch tab={mediaTab} onChange={setMediaTab} />
          {mediaTab === 'clips' ? (
            <ProfileClipList
              clips={user.clips}
              isAppMode={isAppMode}
              canRemove={isCurrentUser || hasAdminCapabilities(user.username)}
              onRemove={setClipToArchive}
              showArchived={showArchived}
              onUnarchive={handleUnarchiveClip}
            />
          ) : user.videos && user.videos.length > 0 ? (
          <div>
            {(() => {
              const activeVideos = user.videos.filter(video => !videoIsArchived(video));
              const archivedVideos = user.videos.filter(video => videoIsArchived(video));

              return (
                <>
                  {/* Active Videos Section */}
                  {activeVideos.length > 0 && (
                    <>
                      <h2 className="text-xl font-semibold mb-3">Uploaded Videos</h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                        {activeVideos
                          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                          .map(video => (
                            <div key={video.videoId} className="border rounded-lg overflow-hidden bg-gray-50">
                              <div className="relative pb-[56.25%] h-0">
                                <iframe
                                  className="absolute top-0 left-0 w-full h-full"
                                  src={video.url}
                                  title={video.title}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                ></iframe>
                              </div>
                              <div className="p-3">
                                <div className="flex justify-between items-center">
                                  <Link to={`${isAppMode ? '/video' : '/legacy/video'}/${video.videoId}`} className="flex-grow hover:text-blue-600">
                                    <h3 className="font-semibold line-clamp-1 text-gray-900">
                                      {video.title}
                                    </h3>
                                    <div className="mt-2 text-sm text-gray-500">
                                      <span>{new Date(video.createdAt).toLocaleDateString()}</span>
                                    </div>
                                  </Link>
                                  <div className="flex items-center">
                                    <span className="text-sm text-gray-500 mr-2">👍 {video.upvotes}</span>
                                    {/* Toggle Featured button - only for moderators and above */}
                                    {appUser && ['Moderator', 'Admin', 'Owner'].includes(appUser.status) && (
                                      <button
                                        onClick={() => handleToggleFeatured(video.videoId)}
                                        className={`text-xs text-white rounded p-1.5 mr-1 ${videoIsFeatured(video)
                                            ? 'bg-yellow-600 hover:bg-yellow-700'
                                            : 'bg-blue-600 hover:bg-blue-700'
                                          }`}
                                        title={videoIsFeatured(video) ? 'Remove from Featured' : 'Add to Featured'}
                                      >
                                        <FaStar />
                                      </button>
                                    )}
                                    {(isCurrentUser || hasAdminCapabilities(video.submitter)) && (
                                      <button
                                        onClick={() => {
                                          setVideoToArchive(video);
                                          setShowArchiveModal(true);
                                        }}
                                        className="text-xs text-white bg-red-600 hover:bg-red-700 rounded p-1.5"
                                        title="Remove video"
                                      >
                                        <FaTrashAlt />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </>
                  )}

                  {/* Archived Videos Section - only show when viewing archived videos */}
                  {showArchived && archivedVideos.length > 0 && (
                    <>
                      <h2 className="text-xl font-semibold mb-3 text-red-800">Archived Videos</h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {archivedVideos
                          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                          .map(video => (
                            <div key={video.videoId} className="border rounded-lg overflow-hidden bg-red-50 border-red-200">
                              <div className="relative pb-[56.25%] h-0">
                                <iframe
                                  className="absolute top-0 left-0 w-full h-full"
                                  src={video.url}
                                  title={video.title}
                                  frameBorder="0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                ></iframe>
                                {/* Unarchive button overlay for users with proper permissions */}
                                {canUnarchiveVideo(video) && (
                                  <div className="absolute top-2 right-2">
                                    <button
                                      onClick={() => handleUnarchiveVideo(video.videoId)}
                                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-semibold shadow-lg"
                                      title="Unarchive video"
                                    >
                                      Unarchive
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className="p-3">
                                <div className="flex justify-between items-center">
                                  <Link to={`${isAppMode ? '/video' : '/legacy/video'}/${video.videoId}`} className="flex-grow hover:text-blue-600">
                                    <h3 className="font-semibold line-clamp-1 text-red-800">
                                      {video.title}
                                    </h3>
                                    <div className="mt-2 text-sm text-gray-500">
                                      <span>{new Date(video.createdAt).toLocaleDateString()}</span>
                                    </div>
                                    {/* Show archive reason */}
                                    <div className="mt-1 text-xs text-red-600">
                                      {video.description}
                                    </div>
                                  </Link>
                                  <div className="flex items-center">
                                    <span className="text-sm text-gray-500 mr-2">👍 {video.upvotes}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                </>
              );
            })()}
          </div>
          ) : (
            <p className="text-gray-500 text-center">
              {showArchived ? 'No videos found.' : 'No videos uploaded yet.'}
            </p>
          )}
        </div>
      </div>

      {clipToArchive && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-4 relative">
            <button
              onClick={() => setClipToArchive(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              aria-label="Close"
            >
              <FaTimes size={20} />
            </button>
            <h2 className="text-xl font-bold mb-4 text-center text-white">Confirm Removal</h2>
            <div className="bg-gray-700 p-4 rounded-lg mb-4 text-gray-200">
              <p>Are you sure you want to remove this clip? It will no longer appear on your profile or in the clips feed.</p>
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setClipToArchive(null)}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => handleArchiveClip(clipToArchive.clipId)}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive confirmation modal */}
      {showArchiveModal && videoToArchive && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-4 relative">
            <button
              onClick={() => {
                setShowArchiveModal(false);
                setVideoToArchive(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              aria-label="Close"
            >
              <FaTimes size={20} />
            </button>

            <h2 className="text-xl font-bold mb-4 text-center text-white">Confirm Removal</h2>
            <div className="bg-gray-700 p-4 rounded-lg mb-4 text-gray-200">
              <p className="mb-4">
                Are you sure you want to remove this video? It will no longer appear in the main feed.
              </p>
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => {
                  setShowArchiveModal(false);
                  setVideoToArchive(null);
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => handleArchiveVideo(videoToArchive.videoId)}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status change confirmation modal */}
      {statusModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-4 relative">
            <button
              onClick={() => {
                setStatusModalOpen(false);
                setStatusChangeError("");
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              aria-label="Close"
            >
              <FaTimes size={20} />
            </button>

            <h2 className="text-xl font-bold mb-4 text-center text-white">Confirm Status Change</h2>

            {statusChangeError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {statusChangeError}
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => {
                  setStatusModalOpen(false);
                  setStatusChangeError("");
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                disabled={statusChangeLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleChangeStatus}
                className={`bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center ${statusChangeLoading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                disabled={statusChangeLoading}
              >
                {statusChangeLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Home component with videos.
// hallSlug / hallDisplayName default to the Commons hall. The same component
// is reused by HallPage for @user and /:slug routes, with those props set.
const Home = ({
  isAuthenticated,
  appUser,
  authReady = true,
  jumpDate = null,
  onJumpDateHandled,
  onStatusChange,
  hallSlug = 'commons',
  hallDisplayName = 'Commons',
  clipsMode = false,
  clipsPage = false,
  onEnterClipsMode
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAppMode = !location.pathname.startsWith('/legacy');
  const isUserHall = String(hallSlug || '').startsWith('@');
  const isHallOwner = !!(appUser && (
    appUser.status === 'Owner' ||
    (isUserHall && `@${appUser.username}`.toLowerCase() === String(hallSlug).toLowerCase())
  ));
  const canManageHallTv = isUserHall
    ? isHallOwner
    : !!(appUser && ['Moderator', 'Admin', 'Owner'].includes(appUser.status));
  const [videoHistory, setVideoHistory] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [submitters, setSubmitters] = useState({});
  const [submitterStatuses, setSubmitterStatuses] = useState({}); // Add state for submitter statuses
  const [votes, setVotes] = useState({});
  const [isVoting, setIsVoting] = useState(false);
  const [shareMessage, setShareMessage] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [videoToArchive, setVideoToArchive] = useState(null);
  const [isCurrentVideoSubmitter, setIsCurrentVideoSubmitter] = useState(false);
  // Add a mapping between URLs and database videoIds
  const [videoDbIds, setVideoDbIds] = useState({});
  const [videoTimes, setVideoTimes] = useState({});
  // Add state to track featured status of videos
  const [videoFeaturedStatus, setVideoFeaturedStatus] = useState({});
  const [videoMeta, setVideoMeta] = useState({});
  const [clipsFromLink, setClipsFromLink] = useState(false);
  const feedRequestIdRef = useRef(0);
  const pendingClipFrontRef = useRef(null);
  const fetchClipsFeedRef = useRef(async () => {});
  const lastHomeVideosRef = useRef(null);
  const prevClipsModeRef = useRef(clipsMode);
  const videoHistoryRef = useRef(videoHistory);
  const videoMetaRef = useRef(videoMeta);
  const videoDbIdsRef = useRef(videoDbIds);
  const videoFeaturedStatusRef = useRef(videoFeaturedStatus);
  const submittersRef = useRef(submitters);
  const currentIndexRef = useRef(currentIndex);

  useEffect(() => {
    videoHistoryRef.current = videoHistory;
  }, [videoHistory]);

  useEffect(() => {
    videoMetaRef.current = videoMeta;
  }, [videoMeta]);

  useEffect(() => {
    videoDbIdsRef.current = videoDbIds;
  }, [videoDbIds]);

  useEffect(() => {
    videoFeaturedStatusRef.current = videoFeaturedStatus;
  }, [videoFeaturedStatus]);

  useEffect(() => {
    submittersRef.current = submitters;
  }, [submitters]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const loadVotesForUrl = async (url) => {
    if (!url) return;
    const youtubeId = feedYoutubeId(url);
    try {
      const votesResponse = await fetch(`/api/videos/${youtubeId}/votes`, {
        credentials: 'include'
      });
      if (votesResponse.ok) {
        const votesData = await votesResponse.json();
        setVotes((prev) => ({
          ...prev,
          [youtubeId]: {
            upvotes: votesData.upvotes,
            userVote: votesData.userVote
          }
        }));
      }
    } catch (votesError) {
      console.error('Error fetching votes:', votesError);
    }
  };

  const applyFeedVideos = (videos, { startIndex = 0, allowEmpty = false, asClips = false } = {}) => {
    const showClips = !!(clipsPage || clipsMode || asClips);
    if (!videos || videos.length === 0) {
      // Never blank the TV unless this is the initial home load with nothing at all
      if (!allowEmpty && videoHistoryRef.current.length > 0) {
        return false;
      }
      setVideoHistory([]);
      setCurrentIndex(-1);
      setVideoDbIds({});
      setVideoTimes({});
      setVideoFeaturedStatus({});
      setVideoMeta({});
      setSubmitters({});
      if (onStatusChange) {
        onStatusChange(showClips
          ? { label: 'Clips', date: formatLocalDate() }
          : { label: '', date: '' });
      }
      return true;
    }

    const urls = [];
    const dbIds = {};
    const times = {};
    const featured = {};
    const meta = {};
    const submitterMap = {};

    videos.forEach((video) => {
      const url = video.clipId != null ? `${video.url}#c${video.clipId}` : video.url;
      urls.push(url);
      dbIds[url] = video.videoId;
      if (video.createdAt) times[url] = video.createdAt;
      featured[url] = videoIsFeatured(video);
      meta[url] = {
        createdAt: video.createdAt || null,
        liveStatus: video.liveStatus || null,
        clipId: video.clipId || null,
        startSeconds: video.startSeconds || 0,
        endSeconds: video.endSeconds || 0
      };
      if (video.submitter) submitterMap[url] = video.submitter;
      if (video.clipId != null && video.title) rememberClipTitle(video.clipId, video.title);
      if (video.videoId != null) {
        const vodTitle = video.videoTitle || (!video.clipId ? video.title : '');
        if (vodTitle) rememberVideoTitle(video.videoId, vodTitle);
      }
    });

    const safeIndex = Math.max(0, Math.min(startIndex, urls.length - 1));
    setVideoHistory(urls);
    setCurrentIndex(safeIndex);
    setVideoDbIds(dbIds);
    setVideoTimes(times);
    setVideoFeaturedStatus(featured);
    setVideoMeta(meta);
    setSubmitters(submitterMap);
    loadVotesForUrl(urls[safeIndex]);
    if (!showClips) {
      lastHomeVideosRef.current = videos;
    }
    if (onStatusChange) {
      if (showClips) {
        const created = meta[urls[safeIndex]]?.createdAt;
        onStatusChange({
          label: 'Clips',
          date: created ? formatLocalDate(new Date(created)) : formatLocalDate()
        });
      } else {
        onStatusChange(statusForVideo(meta[urls[safeIndex]]));
      }
    }
    return true;
  };

  const applyLinkedTvItem = useCallback((item) => {
    const video = item?.video;
    if (!video?.url) return;
    const clip = item.clip || null;

    if (clip) {
      const row = {
        url: video.url,
        videoId: video.videoId,
        createdAt: video.createdAt,
        liveStatus: video.liveStatus,
        submitter: clip.submitter || video.submitter,
        title: clip.title,
        videoTitle: video.title,
        clipId: clip.clipId,
        startSeconds: clip.startSeconds,
        endSeconds: clip.endSeconds
      };
      pendingClipFrontRef.current = item;
      setClipsFromLink(true);
      if (typeof onEnterClipsMode === 'function') onEnterClipsMode();
      applyFeedVideos([row], { startIndex: 0, asClips: true });
      if (clipsMode || clipsPage) fetchClipsFeedRef.current();
      return;
    }

    const momentSeconds = Math.max(0, Math.floor(Number(item.momentSeconds) || 0));
    const playUrl = feedPlayUrl(video.url);
    const urlKey = momentSeconds > 0 ? `${playUrl}#t${momentSeconds}` : playUrl;

    const hist = videoHistoryRef.current.slice();
    let idx = hist.indexOf(urlKey);
    if (idx < 0) {
      idx = currentIndexRef.current >= 0 ? currentIndexRef.current + 1 : hist.length;
      hist.splice(idx, 0, urlKey);
      videoHistoryRef.current = hist;
      setVideoHistory(hist);
    }
    currentIndexRef.current = idx;
    setCurrentIndex(idx);

    setVideoDbIds((prev) => ({ ...prev, [urlKey]: video.videoId }));
    setVideoTimes((prev) => ({ ...prev, [urlKey]: video.createdAt || prev[urlKey] }));
    setVideoFeaturedStatus((prev) => ({ ...prev, [urlKey]: videoIsFeatured(video) }));
    setVideoMeta((prev) => ({
      ...prev,
      [urlKey]: {
        createdAt: video.createdAt || null,
        liveStatus: video.liveStatus || null,
        clipId: null,
        startSeconds: 0,
        endSeconds: 0,
        momentSeconds
      }
    }));
    if (video.submitter) {
      setSubmitters((prev) => ({ ...prev, [urlKey]: video.submitter }));
    }
    if (video.videoId != null && video.title) rememberVideoTitle(video.videoId, video.title);
    loadVotesForUrl(urlKey);
    if (onStatusChange) {
      onStatusChange(statusForVideo({
        liveStatus: video.liveStatus,
        createdAt: video.createdAt
      }));
    }
  }, [clipsMode, clipsPage, onStatusChange, onEnterClipsMode]);

  const fetchHomeFeed = async () => {
    const requestId = ++feedRequestIdRef.current;
    try {
      const response = await fetch(`/api/videos/feed?mode=home&hall=${encodeURIComponent(hallSlug)}`);
      if (!response.ok) {
        throw new Error(`Feed request failed: ${response.status}`);
      }
      const data = await response.json();
      if (requestId !== feedRequestIdRef.current) return;
      applyFeedVideos(data.videos || [], { allowEmpty: true });
    } catch (error) {
      console.error('Error fetching video feed:', error);
      if (requestId !== feedRequestIdRef.current) return;
      applyFeedVideos([], { allowEmpty: true });
    }
  };

  const mapClipsToFeed = (clips) => (clips || []).filter((clip) => (
    isClipInsideYoutubeArchive(clip.startSeconds)
  )).map((clip) => ({
    url: clip.url,
    videoId: clip.videoId,
    createdAt: clip.createdAt,
    liveStatus: clip.liveStatus,
    submitter: clip.submitter || clip.videoSubmitter,
    title: clip.title,
    videoTitle: clip.videoTitle,
    clipId: clip.clipId,
    startSeconds: clip.startSeconds,
    endSeconds: clip.endSeconds
  }));

  const fetchClipsFeed = async (dateStr) => {
    const requestId = ++feedRequestIdRef.current;
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (!clipsPage) params.set('hall', hallSlug);
      if (dateStr) {
        const { from, to } = localDayBounds(dateStr);
        params.set('date', dateStr);
        params.set('from', from);
        params.set('to', to);
      }
      const response = await fetch(`/api/clips/feed?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Clips feed request failed: ${response.status}`);
      }
      const data = await response.json();
      if (requestId !== feedRequestIdRef.current) return;
      let videos = mapClipsToFeed(data.clips);
      const frontItem = pendingClipFrontRef.current;
      pendingClipFrontRef.current = null;
      if (frontItem?.clip && frontItem.video?.url) {
        videos = clipsFeedWithLinkedFirst(videos, {
          url: frontItem.video.url,
          videoId: frontItem.video.videoId,
          createdAt: frontItem.video.createdAt,
          liveStatus: frontItem.video.liveStatus,
          submitter: frontItem.video.submitter,
          title: frontItem.clip.title,
          videoTitle: frontItem.video.title,
          clipId: frontItem.clip.clipId,
          startSeconds: frontItem.clip.startSeconds,
          endSeconds: frontItem.clip.endSeconds
        });
      }
      applyFeedVideos(videos, { allowEmpty: true, asClips: true, startIndex: 0 });
    } catch (error) {
      console.error('Error fetching clips feed:', error);
      if (requestId !== feedRequestIdRef.current) return;
      if (pendingClipFrontRef.current?.clip) {
        pendingClipFrontRef.current = null;
        return;
      }
      applyFeedVideos([], { allowEmpty: true, asClips: true });
    }
  };
  fetchClipsFeedRef.current = fetchClipsFeed;

  const findIndexForDate = (dateStr, history = videoHistoryRef.current, meta = videoMetaRef.current) => {
    if (!dateStr) return -1;
    return history.findIndex((url) => {
      const createdAt = meta[url]?.createdAt;
      if (!createdAt) return false;
      const created = new Date(createdAt);
      const local = formatLocalDate(created);
      const utc = created.toISOString().slice(0, 10);
      return local === dateStr || utc === dateStr;
    });
  };

  const jumpToDate = async (dateStr) => {
    if (!dateStr) return;

    const existingIndex = findIndexForDate(dateStr);
    if (existingIndex >= 0) {
      setCurrentIndex(existingIndex);
      if (onJumpDateHandled) onJumpDateHandled();
      return;
    }

    if (clipsPage || clipsMode) {
      const requestId = ++feedRequestIdRef.current;
      try {
        const { from, to } = localDayBounds(dateStr);
        const params = new URLSearchParams({
          date: dateStr,
          from,
          to,
          limit: '100'
        });
        if (!clipsPage) params.set('hall', hallSlug);
        const response = await fetch(`/api/clips/feed?${params.toString()}`);
        if (!response.ok) {
          throw new Error(`Clips feed request failed: ${response.status}`);
        }
        const data = await response.json();
        if (requestId !== feedRequestIdRef.current) return;
        const dayClips = mapClipsToFeed(data.clips);
        if (dayClips.length === 0) {
          if (onJumpDateHandled) onJumpDateHandled();
          return;
        }
        applyFeedVideos(dayClips, { startIndex: 0 });
      } catch (error) {
        console.error('Error jumping to clip date:', error);
      } finally {
        if (onJumpDateHandled) onJumpDateHandled();
      }
      return;
    }

    // Fetch that day without clearing the current TV if the day is empty
    const requestId = ++feedRequestIdRef.current;
    try {
      const { from, to } = localDayBounds(dateStr);
      const params = new URLSearchParams({
        mode: 'day',
        date: dateStr,
        from,
        to,
        hall: hallSlug
      });
      const response = await fetch(`/api/videos/feed?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Feed request failed: ${response.status}`);
      }
      const data = await response.json();
      if (requestId !== feedRequestIdRef.current) return;

      const dayVideos = data.videos || [];
      if (dayVideos.length === 0) {
        // Keep whatever is playing; do not blank the TV
        if (onJumpDateHandled) onJumpDateHandled();
        return;
      }

      // Merge day videos to the front of the home list (deduped), then jump
      const currentUrls = videoHistoryRef.current;
      const currentMeta = videoMetaRef.current;
      const currentDbIds = videoDbIdsRef.current;
      const currentFeatured = videoFeaturedStatusRef.current;
      const currentSubmitters = submittersRef.current;
      const merged = [...dayVideos];
      const dayIds = new Set(dayVideos.map((v) => v.videoId));
      currentUrls.forEach((url) => {
        const id = currentDbIds[url];
        if (id != null && !dayIds.has(id)) {
          merged.push({
            url,
            videoId: id,
            createdAt: currentMeta[url]?.createdAt,
            liveStatus: currentMeta[url]?.liveStatus,
            isFeatured: currentFeatured[url],
            submitter: currentSubmitters[url]
          });
        }
      });
      applyFeedVideos(merged, { startIndex: 0 });
    } catch (error) {
      console.error('Error jumping to date:', error);
    } finally {
      if (onJumpDateHandled) onJumpDateHandled();
    }
  };

  const handlePrevVideo = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNextVideo = () => {
    if (videoHistory.length === 0) return;
    if (currentIndex < videoHistory.length - 1) {
      setCurrentIndex(currentIndex + 1);
      return;
    }
    // Wrap to start of chronological feed
    setCurrentIndex(0);
  };

  // Initial home feed (lives + featured chronological) or clips playlist
  useEffect(() => {
    const leavingClips = prevClipsModeRef.current && !clipsMode && !clipsPage;
    prevClipsModeRef.current = clipsMode;
    if (clipsPage || clipsMode) {
      fetchClipsFeed();
      return;
    }
    // Restore the video feed immediately so the TV doesn't keep playing the
    // clip until /api/videos/feed returns (often the same live YouTube id).
    if (leavingClips && lastHomeVideosRef.current?.length) {
      applyFeedVideos(lastHomeVideosRef.current, { startIndex: 0 });
    }
    fetchHomeFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hallSlug, clipsMode, clipsPage]);

  useEffect(() => {
    const onSubmitted = (e) => {
      if (clipsPage || clipsMode) return;
      const submitted = String(e.detail?.hallSlug || '').toLowerCase();
      if (submitted && submitted !== String(hallSlug).toLowerCase()) return;
      fetchHomeFeed();
    };
    window.addEventListener(VIDEO_SUBMITTED_EVENT, onSubmitted);
    return () => window.removeEventListener(VIDEO_SUBMITTED_EVENT, onSubmitted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hallSlug, clipsMode, clipsPage]);

  useEffect(() => {
    const onClip = (e) => {
      if (!(clipsPage || clipsMode)) return;
      if (!clipsPage) {
        const submitted = String(e.detail?.hallSlug || '').toLowerCase();
        if (submitted && submitted !== String(hallSlug).toLowerCase()) return;
      }
      fetchClipsFeed();
    };
    window.addEventListener(CLIP_CREATED_EVENT, onClip);
    window.addEventListener(CLIP_DELETED_EVENT, onClip);
    return () => {
      window.removeEventListener(CLIP_CREATED_EVENT, onClip);
      window.removeEventListener(CLIP_DELETED_EVENT, onClip);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hallSlug, clipsMode, clipsPage]);

  // Calendar date jump from header
  useEffect(() => {
    if (!jumpDate) return;
    jumpToDate(jumpDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpDate]);

  // Keep header status in sync with the current video
  useEffect(() => {
    if (!onStatusChange) return;
    if (clipsPage || clipsMode || clipsFromLink) {
      if (currentIndex < 0 || currentIndex >= videoHistory.length) {
        onStatusChange({ label: 'Clips', date: formatLocalDate() });
        return;
      }
      const created = videoMeta[videoHistory[currentIndex]]?.createdAt;
      onStatusChange({
        label: 'Clips',
        date: created ? formatLocalDate(new Date(created)) : formatLocalDate()
      });
      return;
    }
    if (currentIndex < 0 || currentIndex >= videoHistory.length) {
      onStatusChange({ label: '', date: '' });
      return;
    }
    const url = videoHistory[currentIndex];
    onStatusChange(statusForVideo(videoMeta[url]));
  }, [currentIndex, videoHistory, videoMeta, onStatusChange, clipsMode, clipsPage, clipsFromLink]);

  useEffect(() => {
    if (!clipsMode && !clipsPage) setClipsFromLink(false);
  }, [clipsMode, clipsPage]);

  // Prefetch votes when navigating within the loaded feed
  useEffect(() => {
    if (currentIndex < 0 || currentIndex >= videoHistory.length) return;
    loadVotesForUrl(videoHistory[currentIndex]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, videoHistory]);

  // Add handleVote function
  const handleVote = async (videoId, voteType) => {
    if (!isAuthenticated) {
      alert('Please log in to vote');
      return;
    }

    if (isVoting) return;

    setIsVoting(true);
    try {
      console.log(`Voting ${voteType} on video ID: ${videoId}`);

      const response = await fetch(`/api/videos/${videoId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ voteType })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Vote response:', data);

        // Update the votes state with the new vote count and user vote type
        setVotes(prev => {
          const newVotes = {
            ...prev,
            [videoId]: {
              upvotes: data.voteCount,
              userVote: data.message === 'Vote removed' ? null : voteType
            }
          };
          console.log('Updated votes state:', newVotes);
          return newVotes;
        });
      } else {
        const error = await response.json();
        console.error('Vote error:', error);
        alert(error.error || 'Failed to vote');
      }
    } catch (error) {
      console.error('Error voting:', error);
      alert('Failed to vote');
    } finally {
      setIsVoting(false);
    }
  };

  // Fallback if LiveWorld cannot build a db-id link (clipboard only; no Windows share sheet).
  const handleShare = () => {
    if (currentIndex >= 0 && currentIndex < videoHistory.length) {
      const url = videoHistory[currentIndex];
      const dbVideoId = videoDbIds[url];
      const meta = videoMeta[url] || {};
      const sharePath = meta.clipId
        ? (isAppMode ? buildClipPath(meta.clipId) : `/legacy${buildClipPath(meta.clipId)}`)
        : `${isAppMode ? '/video' : '/legacy/video'}/${dbVideoId}`;
      copyToClipboard(`${window.location.origin}${sharePath}`);
    }
  };

  // Helper function for copying to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        showShareMessage();
      })
      .catch(() => {
        // Fallback method
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showShareMessage();
      });
  };

  // Function to show share success message
  const showShareMessage = () => {
    setShareMessage(true);
    setTimeout(() => {
      setShareMessage(false);
    }, 2000);
  };

  // Function to fetch submitter status for permission checking
  const getSubmitterStatus = async (username) => {
    // Return cached status if available
    if (submitterStatuses[username] !== undefined) {
      return submitterStatuses[username];
    }

    try {
      const response = await fetch(`/api/users/${username}`);
      if (response.ok) {
        const userData = await response.json();
        const status = userData.status || '';
        // Cache the status
        setSubmitterStatuses(prev => ({
          ...prev,
          [username]: status
        }));
        return status;
      }
    } catch (error) {
      console.error('Error fetching submitter status:', error);
    }

    // Default to empty string if fetch fails
    setSubmitterStatuses(prev => ({
      ...prev,
      [username]: ''
    }));
    return '';
  };

  // Add hasAdminCapabilities function with proper hierarchy checking
  const hasAdminCapabilities = (videoUsername) => {
    // If user is not authenticated, they don't have admin capabilities
    if (!isAuthenticated || !appUser) return false;

    // If current user is the video submitter, allow deleting own videos
    if (appUser.username === videoUsername) return true;

    const currentUserStatus = appUser?.status || '';

    // Only admins, owners, and moderators can have admin capabilities
    if (!['Admin', 'Owner', 'Moderator'].includes(currentUserStatus)) return false;

    // Owner can delete any video
    if (currentUserStatus === 'Owner') return true;

    // Get cached submitter status
    const submitterStatus = submitterStatuses[videoUsername];

    // If we don't have the status yet, be conservative and don't show the button
    if (submitterStatus === undefined) {
      // Trigger fetching the status in the background
      getSubmitterStatus(videoUsername);
      return false;
    }

    // Admins can delete videos from moderators and regular users, but not from owners or other admins
    if (currentUserStatus === 'Admin') {
      return submitterStatus !== 'Admin' && submitterStatus !== 'Owner';
    }

    // Moderators can only delete videos from regular users (no special status)
    if (currentUserStatus === 'Moderator') {
      return !submitterStatus || (submitterStatus !== 'Moderator' &&
        submitterStatus !== 'Admin' &&
        submitterStatus !== 'Owner' &&
        submitterStatus !== 'Banned');
    }

    return false;
  };

  // Add handleArchiveVideo function
  const handleArchiveVideo = async (videoId) => {
    try {
      const numericVideoId = videoToArchive ? videoToArchive.videoId : videoId;
      const clipId = videoToArchive?.clipId;

      const response = clipId
        ? await fetch(`/api/clips/${encodeURIComponent(clipId)}`, {
            method: 'DELETE',
            credentials: 'include'
          })
        : await fetch(`/api/videos/${numericVideoId}/archive`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            }
          });

      if (response.ok) {
        if (clipId) {
          window.dispatchEvent(new CustomEvent(CLIP_DELETED_EVENT, {
            detail: { clipId }
          }));
        }
        const targetUrl = videoToArchive && videoToArchive.url;
        if (targetUrl) {
          setVideoHistory((prevHistory) => {
            const next = prevHistory.filter((url) => url !== targetUrl);
            setCurrentIndex((idx) => {
              if (next.length === 0) return -1;
              if (idx >= next.length) return next.length - 1;
              return Math.max(0, idx);
            });
            return next;
          });
        }

        setShowArchiveModal(false);
        setVideoToArchive(null);
      } else {
        const errorData = await response.json();
        alert(errorData.error || (clipId ? 'Failed to remove clip' : 'Failed to archive video'));
      }
    } catch (error) {
      console.error('Error removing item:', error);
      alert(videoToArchive?.clipId ? 'Failed to remove clip. Please try again.' : 'Failed to archive video. Please try again.');
    }
  };

  // Add handleToggleFeatured function
  const handleToggleFeatured = async (videoId) => {
    // Capture the videoUrl BEFORE the async operation to prevent race condition
    const videoUrl = videoHistory[currentIndex];
    
    try {
      const response = await fetch(`/api/videos/${videoId}/toggle-featured`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update the featured status in state using the captured videoUrl
        if (videoUrl) {
          setVideoFeaturedStatus(prev => ({
            ...prev,
            [videoUrl]: data.isFeatured
          }));
        }
        // Don't show success alert, only update state
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to toggle featured status');
      }
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert('Failed to toggle featured status. Please try again.');
    }
  };

  // Update archive modal to check if current user is the submitter
  const handleShowArchiveModal = (videoUrl, videoId, clipId = null) => {
    // Get the database videoId from our mapping if available
    const dbVideoId = videoDbIds[videoUrl];
    const videoSubmitter = submitters[videoUrl];
    setIsCurrentVideoSubmitter(videoSubmitter && appUser && videoSubmitter === appUser.username);

    setVideoToArchive({
      videoId: dbVideoId || videoId,
      url: videoUrl,
      clipId: clipId || null
    });

    setShowArchiveModal(true);
  };

  return (
    <div className="relative w-full bg-black flex flex-col">
      <Helmet>
        <title>BIGPLAYS.GG - CS2 Highlights and Clips</title>
        <meta property="og:title" content="BIGPLAYS.GG - CS2 Highlights and Clips" />
        <meta property="og:description" content="Watch, share and discover the best Counter-Strike 2 highlights. Vote on your favorites and submit your own!" />
        <meta property="og:url" content="https://bigplays.gg/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="BIGPLAYS.GG - CS2 Highlights and Clips" />
        <meta name="twitter:description" content="Watch, share and discover the best Counter-Strike 2 highlights. Vote on your favorites and submit your own!" />
      </Helmet>

      {/* Share confirmation toast */}
      {shareMessage && (
        <div className="fixed top-16 left-0 w-full z-50 flex justify-center pointer-events-none">
          <div className="bg-green-600 bg-opacity-90 rounded-md px-4 py-2 text-sm text-white shadow-lg">
            Link copied to clipboard!
          </div>
        </div>
      )}

      {isAppMode && (() => {
        // The hall itself should load regardless of whether the active filter
        // has any videos — users still need to walk around, chat, etc. When no
        // video is available we render LiveWorld with empty video props so the
        // TV overlay is simply hidden (see `{videoUrl && …}` guard in LiveWorld).
        const hasVideo = currentIndex >= 0 && currentIndex < videoHistory.length;
        const currentUrl = hasVideo ? videoHistory[currentIndex] : '';
        const playUrl = feedPlayUrl(currentUrl);
        const currentYtId = feedYoutubeId(currentUrl);
        const clipMeta = hasVideo ? (videoMeta[currentUrl] || {}) : {};
        return (
          <LiveWorld
            videoId={currentYtId}
            videoUrl={playUrl}
            isLive={videoMeta[currentUrl]?.liveStatus === 'live'}
            user={appUser}
            authReady={authReady}
            hallSlug={hallSlug}
            hallDisplayName={hallDisplayName}
            votes={votes}
            isVoting={isVoting}
            isAuthenticated={isAuthenticated}
            onVote={hasVideo ? handleVote : undefined}
            onNext={hasVideo ? handleNextVideo : undefined}
            onPrev={hasVideo ? handlePrevVideo : undefined}
            canGoPrev={hasVideo && currentIndex > 0}
            onShare={hasVideo ? handleShare : undefined}
            dbVideoId={hasVideo ? videoDbIds[currentUrl] : undefined}
            momentStartSeconds={clipMeta.momentSeconds || 0}
            clipId={clipMeta.clipId || null}
            clipStartSeconds={clipMeta.startSeconds || 0}
            clipEndSeconds={clipMeta.endSeconds || 0}
            onPlayHallMedia={applyLinkedTvItem}
            metaBar={hasVideo ? (
              <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                {submitters[currentUrl] && (
                  <Link to={`${isAppMode ? '/user' : '/legacy/user'}/${submitters[currentUrl]}`}>
                    <span className="font-bold text-white whitespace-nowrap hover:underline">
                      {submitters[currentUrl]}
                    </span>
                  </Link>
                )}
                {videoTimes[currentUrl] && (
                  <span className="text-gray-400 whitespace-nowrap">
                    {formatTimeAgo(videoTimes[currentUrl])}
                  </span>
                )}
                {isAuthenticated &&
                  appUser &&
                  canManageHallTv &&
                  !clipMeta.clipId &&
                  videoFeaturedStatus.hasOwnProperty(currentUrl) && (
                    <button
                      onClick={() => {
                        const dbVideoId = videoDbIds[currentUrl];
                        if (!dbVideoId) {
                          alert('Missing video id — refresh and try again');
                          return;
                        }
                        handleToggleFeatured(dbVideoId);
                      }}
                      className={videoFeaturedStatus[currentUrl] === true ? 'text-yellow-500 hover:text-yellow-600' : 'text-blue-500 hover:text-blue-600'}
                      title={videoFeaturedStatus[currentUrl] === true ? 'Remove from Featured' : 'Add to Featured'}
                    >
                      <FaStar className="w-3 h-3" />
                    </button>
                  )}
                {isAuthenticated &&
                  appUser &&
                  (
                    submitters[currentUrl]
                      ? hasAdminCapabilities(submitters[currentUrl])
                      : ['Moderator', 'Admin', 'Owner'].includes(appUser.status)
                  ) && (
                    <button
                      onClick={() => {
                        handleShowArchiveModal(currentUrl, feedYoutubeId(currentUrl), clipMeta.clipId || null);
                      }}
                      className="text-red-600 hover:text-red-700"
                      title={clipMeta.clipId ? 'Remove clip' : 'Remove video'}
                    >
                      <FaTrashAlt className="w-3 h-3" />
                    </button>
                  )}
              </div>
            ) : null}
          />
        );
      })()}

      {/* Video submission modal */}
      <VideoSubmissionModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        isAuthenticated={isAuthenticated}
        appUser={appUser}
        hallSlug={hallSlug}
      />

      {/* Archive confirmation modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-4 relative">
            <button
              onClick={() => setShowArchiveModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              aria-label="Close"
            >
              <FaTimes size={20} />
            </button>

            <h2 className="text-xl font-bold mb-4 text-center text-white">Confirm Removal</h2>
            <div className="bg-gray-700 p-4 rounded-lg mb-4 text-gray-200">
              <p className="mb-4">
                {videoToArchive?.clipId
                  ? 'Are you sure you want to remove this clip? The original video will stay in the feed.'
                  : 'Are you sure you want to remove this video? It will no longer appear in the main feed.'}
              </p>
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => handleArchiveVideo()}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Breakpoint at which the search bar starts expanded (matches Tailwind's sm: = 640px)
const SEARCH_EXPAND_BREAKPOINT = 640;

// UserSearch component
const UserSearch = ({ onExpandChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Start expanded on wider screens; collapsed on narrow mobile
  const [isExpanded, setIsExpanded] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= SEARCH_EXPAND_BREAKPOINT
  );
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const wasWideRef = useRef(
    typeof window !== 'undefined' && window.innerWidth >= SEARCH_EXPAND_BREAKPOINT
  );
  const navigate = useNavigate();
  const location = useLocation();
  const isAppMode = !location.pathname.startsWith('/legacy');

  // Expand on wide screens; collapse back to the icon when rotating to portrait
  // (iOS landscape is often >= 640px, so leaving that breakpoint must shrink).
  useEffect(() => {
    const viewportWidth = () => {
      const inner = window.innerWidth;
      const vv = window.visualViewport?.width;
      if (typeof vv === 'number' && vv > 0) return Math.min(inner, Math.round(vv));
      return inner;
    };
    const syncToViewport = () => {
      const wide = viewportWidth() >= SEARCH_EXPAND_BREAKPOINT;
      const wasWide = wasWideRef.current;
      wasWideRef.current = wide;
      if (wide) {
        setIsExpanded(true);
        if (onExpandChange) onExpandChange(false);
        return;
      }
      if (wasWide) {
        setIsExpanded(false);
        setSearchQuery('');
        setSearchResults([]);
        setShowDropdown(false);
        if (onExpandChange) onExpandChange(false);
      }
    };
    const onOrient = () => {
      syncToViewport();
      window.setTimeout(syncToViewport, 150);
      window.setTimeout(syncToViewport, 450);
    };
    window.addEventListener('resize', syncToViewport);
    window.addEventListener('orientationchange', onOrient);
    const vv = window.visualViewport;
    if (vv) vv.addEventListener('resize', syncToViewport);
    return () => {
      window.removeEventListener('resize', syncToViewport);
      window.removeEventListener('orientationchange', onOrient);
      if (vv) vv.removeEventListener('resize', syncToViewport);
    };
  }, [onExpandChange]);

  const expand = () => {
    setIsExpanded(true);
    if (onExpandChange) onExpandChange(true);
    // Defer focus so the input is visible in the DOM first
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const collapse = () => {
    setIsExpanded(false);
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    if (onExpandChange) onExpandChange(false);
  };

  // Collapse when clicking/touching outside — always, even with text, on narrow screens
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
        if (window.innerWidth < SEARCH_EXPAND_BREAKPOINT) {
          collapse();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search for users as the user types
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.trim() === '') {
        setSearchResults([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.get(`/api/users/search?query=${encodeURIComponent(searchQuery)}`);
        setSearchResults(response.data.users.slice(0, 5)); // Limit to 5 results for dropdown
        setShowDropdown(true);
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    if (searchQuery.trim() === '') return;

    // If there's an exact match, navigate directly to that user's profile
    const exactMatch = searchResults.find(
      user => user.username.toLowerCase() === searchQuery.toLowerCase()
    );

    const baseUserPath = isAppMode ? '/user' : '/legacy/user';
    const baseSearchPath = isAppMode ? '/search' : '/legacy/search';

    if (exactMatch) {
      navigate(`${baseUserPath}/${exactMatch.username}`);
    } else {
      // Navigate to search results page
      navigate(`${baseSearchPath}?q=${encodeURIComponent(searchQuery)}`);
    }

    setShowDropdown(false);
  };

  const handleUserSelect = (username) => {
    const baseUserPath = isAppMode ? '/user' : '/legacy/user';
    navigate(`${baseUserPath}/${username}`);
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
  };

  return (
    <div className="relative flex items-center min-w-0" ref={searchRef}>
      {/* Collapsed state: circular magnifying glass icon — shown only on narrow screens */}
      {!isExpanded && (
        <button
          onClick={expand}
          className="flex items-center justify-center w-9 h-9 flex-shrink-0 rounded-full bg-gray-800 border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          aria-label="Search users"
        >
          <FaSearch className="w-4 h-4" />
        </button>
      )}

      {/* Expanded state: full input bar filling available flex space */}
      {isExpanded && (
        <form onSubmit={handleSearchSubmit} className="w-full min-w-0">
          <div className="relative flex items-center">
            <input
              ref={inputRef}
              type="text"
              inputMode="search"
              placeholder="Search users..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full py-1.5 pl-3 pr-16 rounded-full bg-gray-800 border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{ fontSize: '16px' }}
              onFocus={() => searchQuery.trim() !== '' && setShowDropdown(true)}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-9 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <FaTimes className="w-3 h-3" />
              </button>
            )}
            <button
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              <FaSearch className="w-3.5 h-3.5" />
            </button>
          </div>
        </form>
      )}

      {/* Dropdown results — anchored below the search bar */}
      {showDropdown && searchResults.length > 0 && (
        <div className="absolute top-full left-0 z-50 w-full min-w-[180px] mt-1 bg-gray-800 border border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
          <ul>
            {searchResults.map((user) => (
              <li key={user.userId} className="border-b border-gray-700 last:border-b-0">
                <button
                  onClick={() => handleUserSelect(user.username)}
                  className="w-full px-3 py-1.5 flex items-center hover:bg-gray-700 text-left text-sm"
                >
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.username} className="w-6 h-6 rounded-full mr-2" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-600 mr-2 flex items-center justify-center text-sm">
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span>{user.username}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showDropdown && isLoading && (
        <div className="absolute top-full left-0 z-50 w-full min-w-[180px] mt-1 bg-gray-800 border border-gray-700 rounded-md shadow-lg p-3 text-center text-sm">
          Searching...
        </div>
      )}

      {showDropdown && !isLoading && searchQuery && searchResults.length === 0 && (
        <div className="absolute top-full left-0 z-50 w-full min-w-[180px] mt-1 bg-gray-800 border border-gray-700 rounded-md shadow-lg p-3 text-center text-sm">
          No users found
        </div>
      )}
    </div>
  );
};

// SearchResults component
const SearchResults = ({ isAuthenticated }) => {
  const location = useLocation();
  const isAppMode = !location.pathname.startsWith('/legacy');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = new URLSearchParams(location.search).get('q');
    setSearchQuery(query || '');

    const fetchSearchResults = async () => {
      if (!query) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/api/users/search?query=${encodeURIComponent(query)}`);
        setSearchResults(response.data.users);
      } catch (error) {
        console.error('Error fetching search results:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSearchResults();
  }, [location.search]);

  if (isLoading) {
    return <div className="container mx-auto px-4 py-8 text-center">Loading...</div>;
  }

  if (!searchQuery) {
    return <div className="container mx-auto px-4 py-8 text-center">No search query provided</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-2xl font-bold mb-6">
        Search results for "{searchQuery}"
      </h2>

      {searchResults.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-lg mb-4">No users found matching "{searchQuery}"</p>
          <p className="text-gray-400">Try a different search term</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {searchResults.map((user) => (
            <Link
              key={user.userId}
              to={`${isAppMode ? '/user' : '/legacy/user'}/${user.username}`}
              className="bg-gray-800 rounded-lg p-4 flex items-center hover:bg-gray-700 transition-colors"
            >
              {user.avatar ? (
                <img src={user.avatar} alt={user.username} className="w-12 h-12 rounded-full mr-4" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-600 mr-4 flex items-center justify-center text-lg">
                  {user.username.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h3 className="font-semibold text-lg">{user.username}</h3>
                <p className="text-sm text-gray-400">
                  Member since {new Date(user.createdAt).toLocaleDateString()}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// Video page component for sharing individual videos
const VideoPage = ({ appUser, isAuthenticated, authReady = true }) => {
  const { videoId } = useParams();
  const [searchParams] = useSearchParams();
  const momentStartSeconds = parseTimeParam(searchParams.get('t'));
  const location = useLocation();
  const isAppMode = !location.pathname.startsWith('/legacy');
  const [video, setVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [votes, setVotes] = useState({});
  const [isVoting, setIsVoting] = useState(false);
  const [submitter, setSubmitter] = useState(null);
  const [shareMessage, setShareMessage] = useState(false); // For share confirmation
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [canViewArchived, setCanViewArchived] = useState(true); // Permission to view archived video
  const [tvOverride, setTvOverride] = useState(null);
  const navigate = useNavigate();
  const appUserRef = useRef(appUser);
  appUserRef.current = appUser;

  // Function to check if current user can view archived video
  const checkArchivedVideoPermission = (video, currentUser) => {
    if (!videoIsArchived(video)) return true; // Non-archived videos are always viewable
    if (!currentUser) return false; // Not authenticated users can't view archived videos

    const currentUserStatus = currentUser.status || '';
    const submitterStatus = video.submitterStatus || '';

    // Users can always view their own archived videos
    if (currentUser.username === video.submitter) return true;

    // Owner can view anyone's archived videos
    if (currentUserStatus === 'Owner') return true;

    // Admins can view archived videos of moderators and regular users
    if (currentUserStatus === 'Admin') {
      return submitterStatus !== 'Admin' && submitterStatus !== 'Owner';
    }

    // Moderators can view archived videos of regular users only
    if (currentUserStatus === 'Moderator') {
      return !submitterStatus || (submitterStatus !== 'Moderator' &&
        submitterStatus !== 'Admin' &&
        submitterStatus !== 'Owner' &&
        submitterStatus !== 'Banned');
    }

    // Regular users cannot view other people's archived videos
    return false;
  };

  // Fetch once per video id. Do not depend on appUser — XP ticks clone that
  // object, which was remounting LiveWorld in a Loading video / Loading map loop.
  useEffect(() => {
    if (!videoId) return undefined;
    let cancelled = false;

    const fetchVideo = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/videos/${videoId}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch video: ${response.statusText}`);
        }

        const data = await response.json();
        if (cancelled) return;
        if (data.videoId != null && data.title) rememberVideoTitle(data.videoId, data.title);
        setVideo(data);
        setCanViewArchived(checkArchivedVideoPermission(data, appUserRef.current));
        if (data.submitter) setSubmitter(data.submitter);
        if (checkArchivedVideoPermission(data, appUserRef.current)) {
          fetchVotes(videoId);
        }
      } catch (error) {
        console.error('Error fetching video:', error);
        if (!cancelled) {
          setError('Failed to load video. It may have been removed or does not exist.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchVideo();
    return () => { cancelled = true; };
  }, [videoId]);

  useEffect(() => {
    setTvOverride(null);
  }, [videoId]);

  // Re-check archive permission when auth identity changes, without refetching.
  useEffect(() => {
    if (!video) return;
    setCanViewArchived(checkArchivedVideoPermission(video, appUser));
  }, [video, appUser?.userId, appUser?.username, appUser?.status]);

  const fetchVotes = async (id) => {
    try {
      const response = await fetch(`/api/videos/${id}/votes`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVotes(prev => ({
          ...prev,
          [id]: data
        }));
      }
    } catch (error) {
      console.error('Error fetching votes:', error);
    }
  };

  // Single implementation of handleVote function
  const handleVote = async (videoId, voteType) => {
    if (!isAuthenticated) {
      alert('Please log in to vote');
      return;
    }

    if (isVoting) return;

    setIsVoting(true);
    try {
      console.log(`Voting ${voteType} on video ID: ${videoId}`);

      const response = await fetch(`/api/videos/${videoId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ voteType })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Vote response:', data);

        // Update the votes state with the new vote count and user vote type
        setVotes(prev => {
          const newVotes = {
            ...prev,
            [videoId]: {
              upvotes: data.voteCount,
              userVote: data.message === 'Vote removed' ? null : voteType
            }
          };
          console.log('Updated votes state:', newVotes);
          return newVotes;
        });
      } else {
        const error = await response.json();
        console.error('Vote error:', error);
        alert(error.error || 'Failed to vote');
      }
    } catch (error) {
      console.error('Error voting:', error);
      alert('Failed to vote');
    } finally {
      setIsVoting(false);
    }
  };

  // Fallback if LiveWorld cannot build a db-id link (clipboard only; no Windows share sheet).
  const handleShare = () => {
    const clipToShare = tvOverride?.clip;
    const sharePath = clipToShare?.clipId
      ? (isAppMode ? buildClipPath(clipToShare.clipId) : `/legacy${buildClipPath(clipToShare.clipId)}`)
      : `${isAppMode ? '/video' : '/legacy/video'}/${videoId}`;
    copyToClipboard(`${window.location.origin}${sharePath}`);
  };

  // Helper function for copying to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        showShareMessage();
      })
      .catch(() => {
        // Fallback method
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showShareMessage();
      });
  };

  // Function to show share success message
  const showShareMessage = () => {
    setShareMessage(true);
    setTimeout(() => {
      setShareMessage(false);
    }, 2000);
  };

  // Add hasAdminCapabilities function with proper hierarchy checking
  const hasAdminCapabilities = (videoUsername) => {
    // If user is not authenticated, they don't have admin capabilities
    if (!isAuthenticated || !appUser) return false;

    // If current user is the video submitter, allow deleting own videos
    if (appUser.username === videoUsername) return true;

    const currentUserStatus = appUser?.status || '';

    // Only admins, owners, and moderators can have admin capabilities
    if (!['Admin', 'Owner', 'Moderator'].includes(currentUserStatus)) return false;

    // Owner can delete any video
    if (currentUserStatus === 'Owner') return true;

    // Get the submitter's status from the video data
    const submitterStatus = video?.submitterStatus || '';

    // Admins can delete videos from moderators and regular users, but not from owners or other admins
    if (currentUserStatus === 'Admin') {
      return submitterStatus !== 'Admin' && submitterStatus !== 'Owner';
    }

    // Moderators can only delete videos from regular users (no special status)
    if (currentUserStatus === 'Moderator') {
      return !submitterStatus || (submitterStatus !== 'Moderator' &&
        submitterStatus !== 'Admin' &&
        submitterStatus !== 'Owner' &&
        submitterStatus !== 'Banned');
    }

    return false;
  };

  // Add handleArchiveVideo function
  const handleArchiveVideo = async () => {
    try {
      const clipToDelete = tvOverride?.clip;
      if (clipToDelete?.clipId) {
        const response = await fetch(`/api/clips/${encodeURIComponent(clipToDelete.clipId)}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        if (response.ok) {
          window.dispatchEvent(new CustomEvent(CLIP_DELETED_EVENT, {
            detail: { clipId: clipToDelete.clipId }
          }));
          setTvOverride(null);
          setShowArchiveModal(false);
        } else {
          const errorData = await response.json();
          alert(errorData.error || 'Failed to remove clip');
        }
        return;
      }

      const response = await fetch(`/api/videos/${videoId}/archive`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Navigate back to home after successful archive
        navigate(isAppMode ? '/' : '/legacy');
        setShowArchiveModal(false);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to archive video');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      alert(tvOverride?.clip?.clipId ? 'Failed to remove clip. Please try again.' : 'Failed to archive video. Please try again.');
    }
  };

  const handleToggleFeatured = async () => {
    try {
      const response = await fetch(`/api/videos/${videoId}/toggle-featured`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update video state to reflect the change
        setVideo(prev => ({
          ...prev,
          isFeatured: data.isFeatured,
          description: data.isFeatured ? 'Featured' : null
        }));
        // Don't show success alert, only update state
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to toggle featured status');
      }
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert('Failed to toggle featured status. Please try again.');
    }
  };

  // Add handleUnarchiveVideo function
  const handleUnarchiveVideo = async () => {
    try {
      const response = await fetch(`/api/videos/${videoId}/unarchive`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh the page to show the unarchived video
        window.location.reload();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to unarchive video');
      }
    } catch (error) {
      console.error('Error unarchiving video:', error);
      alert('Failed to unarchive video. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <div className="text-2xl font-bold text-white">Loading video...</div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)]">
        <div className="text-2xl font-bold text-white mb-4">
          {error || 'Video not found'}
        </div>
        <button
          onClick={() => navigate(isAppMode ? '/' : '/legacy')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
        >
          Return to Home
        </button>
      </div>
    );
  }

  const playVideo = tvOverride?.video || video;
  const playClip = tvOverride?.clip || null;
  const playMoment = tvOverride ? (tvOverride.momentSeconds || 0) : momentStartSeconds;
  const playSubmitter = playClip?.submitter || tvOverride?.video?.submitter || submitter;

  // Extract YouTube video ID from URL
  const youtubeId = playVideo.url.includes('/embed/')
    ? playVideo.url.split('/embed/')[1].split('?')[0]
    : '';

  // Thumbnail URL based on YouTube ID
  const thumbnailUrl = youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`
    : '';

  return (
    <div className="relative w-full bg-black flex flex-col">
      <Helmet>
        <title>{`${video.title || 'CS2 Highlight'} - BIGPLAYS.GG`}</title>
        <meta property="og:title" content={`${video.title || 'CS2 Highlight'} - BIGPLAYS.GG`} />
        <meta property="og:description" content={`Watch this awesome CS2 highlight${submitter ? ` from ${submitter}` : ''} on BIGPLAYS.GG!`} />
        <meta property="og:url" content={`https://bigplays.gg/video/${videoId}`} />
        {thumbnailUrl && <meta property="og:image" content={thumbnailUrl} />}
        <meta property="og:type" content="video.other" />
        <meta property="og:video" content={video.url} />
        <meta property="og:video:secure_url" content={video.url} />
        <meta property="og:video:type" content="text/html" />
        <meta property="og:video:width" content="1280" />
        <meta property="og:video:height" content="720" />
        <meta name="twitter:card" content="player" />
        <meta name="twitter:title" content={`${video.title || 'CS2 Highlight'} - BIGPLAYS.GG`} />
        <meta name="twitter:description" content={`Watch this awesome CS2 highlight${submitter ? ` from ${submitter}` : ''} on BIGPLAYS.GG!`} />
        {thumbnailUrl && <meta name="twitter:image" content={thumbnailUrl} />}
        <meta name="twitter:player" content={video.url} />
        <meta name="twitter:player:width" content="1280" />
        <meta name="twitter:player:height" content="720" />
      </Helmet>

      {/* Share confirmation toast */}
      {shareMessage && (
        <div className="fixed top-16 left-0 w-full z-50 flex justify-center pointer-events-none">
          <div className="bg-green-600 bg-opacity-90 rounded-md px-4 py-2 text-sm text-white shadow-lg">
            Link copied to clipboard!
          </div>
        </div>
      )}

      {canViewArchived ? (
        isAppMode && video && video.url ? (
          <LiveWorld
            videoId={feedYoutubeId(playVideo.url)}
            videoUrl={feedPlayUrl(playVideo.url)}
            isLive={playVideo?.liveStatus === 'live'}
            user={appUser}
            authReady={authReady}
            hallSlug="commons"
            hallDisplayName="Commons"
            votes={votes}
            isVoting={isVoting}
            isAuthenticated={isAuthenticated}
            onVote={handleVote}
            onShare={handleShare}
            dbVideoId={playVideo.videoId}
            momentStartSeconds={playMoment}
            clipId={playClip?.clipId || null}
            clipStartSeconds={playClip?.startSeconds || 0}
            clipEndSeconds={playClip?.endSeconds || 0}
            onPlayHallMedia={setTvOverride}
            metaBar={
              <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                {playSubmitter && (
                  <Link to={`/user/${playSubmitter}`}>
                    <span className="font-bold text-white whitespace-nowrap hover:underline">
                      {playSubmitter}
                    </span>
                  </Link>
                )}
                {playVideo.createdAt && (
                  <span className="text-gray-400 whitespace-nowrap">
                    {formatTimeAgo(playVideo.createdAt)}
                  </span>
                )}
                {isAuthenticated && playSubmitter && hasAdminCapabilities(playSubmitter) && (
                  <>
                    {playClip ? (
                      <button
                        onClick={() => setShowArchiveModal(true)}
                        className="text-red-600 hover:text-red-700"
                        title="Remove clip"
                      >
                        <FaTrashAlt className="w-3 h-3" />
                      </button>
                    ) : videoIsArchived(video) ? (
                      <button
                        onClick={handleUnarchiveVideo}
                        className="text-green-600 hover:text-green-700"
                        title="Unarchive video"
                      >
                        <FaRedo className="w-3 h-3" />
                      </button>
                    ) : (
                      <>
                        {appUser && ['Moderator', 'Admin', 'Owner'].includes(appUser.status) && (
                          <button
                            onClick={handleToggleFeatured}
                            className={videoIsFeatured(video) ? 'text-yellow-500 hover:text-yellow-600' : 'text-blue-500 hover:text-blue-600'}
                            title={videoIsFeatured(video) ? 'Remove from Featured' : 'Add to Featured'}
                          >
                            <FaStar className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          onClick={() => setShowArchiveModal(true)}
                          className="text-red-600 hover:text-red-700"
                          title="Remove video"
                        >
                          <FaTrashAlt className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            }
          />
        ) : (
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <div className="text-2xl font-bold text-white">Loading...</div>
          </div>
        )
      ) : (
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center text-white p-8">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold mb-2">
              {submitter ? `${submitter}'s video has been removed` : 'This video has been removed'}
            </h2>
          </div>
        </div>
      )}

      {/* Archive confirmation modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-4 relative">
            <button
              onClick={() => setShowArchiveModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              aria-label="Close"
            >
              <FaTimes size={20} />
            </button>

            <h2 className="text-xl font-bold mb-4 text-center text-white">Confirm Removal</h2>
            <div className="bg-gray-700 p-4 rounded-lg mb-4 text-gray-200">
              <p className="mb-4">
                {playClip
                  ? 'Are you sure you want to remove this clip? The original video will stay in the feed.'
                  : 'Are you sure you want to remove this video? It will no longer appear in the main feed.'}
              </p>
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveVideo}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Adds noindex/nofollow to legacy pages so search engines don't index the old version
const LegacyMeta = () => (
  <Helmet>
    <meta name="robots" content="noindex, nofollow" />
  </Helmet>
);

// Redirect helpers that preserve dynamic params from old /app/* URLs
const RedirectAppUser = () => {
  const { username } = useParams();
  return <Navigate to={`/user/${username}`} replace />;
};

const RedirectAppVideo = () => {
  const { videoId } = useParams();
  return <Navigate to={`/video/${videoId}`} replace />;
};

const ClipPage = ({ appUser, isAuthenticated, authReady = true }) => {
  const { clipId } = useParams();
  const location = useLocation();
  const isAppMode = !location.pathname.startsWith('/legacy');
  const [clip, setClip] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [votes, setVotes] = useState({});
  const [isVoting, setIsVoting] = useState(false);
  const [tvOverride, setTvOverride] = useState(null);
  const [sessionHallSlug, setSessionHallSlug] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!clipId) return undefined;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/clips/${encodeURIComponent(clipId)}`, {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch clip: ${response.statusText}`);
        }
        const data = await response.json();
        if (cancelled) return;
        if (data.clipId != null && data.title) rememberClipTitle(data.clipId, data.title);
        if (data.videoId != null && data.videoTitle) rememberVideoTitle(data.videoId, data.videoTitle);
        setClip(data);
      } catch (err) {
        console.error('Error fetching clip:', err);
        if (!cancelled) setError('Failed to load clip. It may have been removed or does not exist.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [clipId]);

  useEffect(() => {
    setTvOverride(null);
  }, [clipId]);

  useEffect(() => {
    if (clip?.hall && !sessionHallSlug) setSessionHallSlug(clip.hall);
  }, [clip, sessionHallSlug]);

  useEffect(() => {
    if (!clip?.videoId) return undefined;
    let cancelled = false;
    fetch(`/api/videos/${clip.videoId}/votes`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setVotes({
          [feedYoutubeId(clip.url)]: {
            upvotes: data.upvotes,
            userVote: data.userVote
          }
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [clip]);

  const handleVote = async (youtubeId, voteType) => {
    if (!isAuthenticated) {
      alert('Please log in to vote');
      return;
    }
    if (isVoting || !clip) return;
    setIsVoting(true);
    try {
      const response = await fetch(`/api/videos/${youtubeId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ voteType })
      });
      if (response.ok) {
        const data = await response.json();
        setVotes((prev) => ({
          ...prev,
          [youtubeId]: {
            upvotes: data.voteCount,
            userVote: data.message === 'Vote removed' ? null : voteType
          }
        }));
      }
    } catch (err) {
      console.error('Error voting:', err);
    } finally {
      setIsVoting(false);
    }
  };

  const handleDeleteClip = async () => {
    const id = tvOverride?.clip?.clipId || clip?.clipId;
    if (!id) return;
    try {
      const response = await fetch(`/api/clips/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.error || 'Failed to remove clip');
        return;
      }
      window.dispatchEvent(new CustomEvent(CLIP_DELETED_EVENT, { detail: { clipId: id } }));
      setShowDeleteModal(false);
      if (clip && String(id) === String(clip.clipId)) {
        navigate(isAppMode ? '/clips' : '/legacy/clips');
      } else {
        setTvOverride(null);
      }
    } catch (err) {
      console.error('Error deleting clip:', err);
      alert('Failed to remove clip. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <div className="text-xl">Loading clip…</div>
      </div>
    );
  }

  if (error || !clip) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] px-4">
        <h1 className="text-2xl font-bold mb-4">Clip not found</h1>
        <p className="text-gray-400 mb-6">{error || 'This clip does not exist.'}</p>
        <button
          type="button"
          onClick={() => navigate(isAppMode ? '/clips' : '/legacy/clips')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
        >
          Browse clips
        </button>
      </div>
    );
  }

  const playVideo = tvOverride?.video || null;
  const playClip = tvOverride ? tvOverride.clip : clip;
  const playUrl = playVideo?.url || clip.url;
  const youtubeId = playVideo
    ? feedYoutubeId(playVideo.url)
    : (clip.youtubeVideoId || feedYoutubeId(clip.url));
  const hallSlug = sessionHallSlug || clip.hall || 'commons';
  const hallDisplayName = clip.hallDisplayName || (hallSlug === 'commons' ? 'Commons' : hallSlug);
  const playTitle = playClip?.title || playVideo?.title || clip.title;
  const playSubmitter = playClip
    ? (playClip.submitter || clip.submitter)
    : (playVideo?.submitter || clip.submitter);
  const playCreatedAt = playClip?.createdAt || playVideo?.createdAt || clip.createdAt;
  const dbVideoId = playVideo?.videoId || clip.videoId;
  const canDeleteThisClip = isAuthenticated && appUser && playClip?.clipId && (
    (playSubmitter && appUser.username === playSubmitter)
    || ['Moderator', 'Admin', 'Owner'].includes(appUser.status)
  );

  return (
    <div className="relative w-full bg-black flex flex-col">
      <Helmet>
        <title>{`${playTitle || 'Clip'} - BIGPLAYS.GG`}</title>
        <meta property="og:title" content={`${playTitle || 'Clip'} - BIGPLAYS.GG`} />
        <meta property="og:type" content="video.other" />
      </Helmet>
      {isAppMode && playUrl ? (
        <LiveWorld
          videoId={youtubeId}
          videoUrl={feedPlayUrl(playUrl)}
          isLive={(playVideo?.liveStatus || clip.liveStatus) === 'live'}
          user={appUser}
          authReady={authReady}
          hallSlug={hallSlug}
          hallDisplayName={hallDisplayName}
          votes={votes}
          isVoting={isVoting}
          isAuthenticated={isAuthenticated}
          onVote={handleVote}
          dbVideoId={dbVideoId}
          momentStartSeconds={tvOverride ? (tvOverride.momentSeconds || 0) : 0}
          clipId={playClip?.clipId || null}
          clipStartSeconds={playClip?.startSeconds || 0}
          clipEndSeconds={playClip?.endSeconds || 0}
          onPlayHallMedia={setTvOverride}
          metaBar={
            <div className="flex items-center gap-2 ml-auto flex-shrink-0">
              <span className="font-bold text-white whitespace-nowrap">{playTitle}</span>
              {playSubmitter && (
                <Link to={`${isAppMode ? '/user' : '/legacy/user'}/${playSubmitter}`}>
                  <span className="text-gray-300 whitespace-nowrap hover:underline">
                    {playSubmitter}
                  </span>
                </Link>
              )}
              {playCreatedAt && (
                <span className="text-gray-400 whitespace-nowrap">
                  {formatTimeAgo(playCreatedAt)}
                </span>
              )}
              {canDeleteThisClip && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="text-red-600 hover:text-red-700"
                  title="Remove clip"
                >
                  <FaTrashAlt className="w-3 h-3" />
                </button>
              )}
            </div>
          }
        />
      ) : (
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-xl">This clip is unavailable.</div>
        </div>
      )}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-4 relative">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              aria-label="Close"
            >
              <FaTimes size={20} />
            </button>
            <h2 className="text-xl font-bold mb-4 text-center text-white">Confirm Removal</h2>
            <div className="bg-gray-700 p-4 rounded-lg mb-4 text-gray-200">
              <p className="mb-4">
                Are you sure you want to remove this clip? The original video will stay in the feed.
              </p>
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteClip}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Reserved slugs (must match server-side perms.RESERVED_SLUGS).
// These paths are handled by their own routes (/submit, /user/:username, etc.)
// so they must never resolve to a hall.
const RESERVED_HALL_SLUGS = new Set([
  'commons', 'api', 'app', 'legacy', 'user', 'users', 'video', 'videos',
  'submit', 'search', 'create-account', 'connect-google', 'admin', 'auth', 'static', 'public',
  'debug', 'halls', 'hall', 'login', 'logout', 'register', 'about',
  'terms', 'privacy', 'report', 'contact', 'help', 'settings', 'profile', 'clip', 'clips'
]);
const HALL_SLUG_REGEX = /^[A-Za-z0-9_-]{2,32}$/;
const USERNAME_HALL_REGEX = /^@[A-Za-z0-9_-]{2,16}$/;

/**
 * Wrapper route component for /:slug and /@:username.
 *
 * Validates the slug client-side (so we can 404 reserved/invalid slugs
 * without a server round trip), then fetches hall info and renders Home
 * with the appropriate hallSlug / hallDisplayName props.
 */
const HallPage = ({ appUser, isAuthenticated, authReady = true, jumpDate, onJumpDateHandled, onStatusChange, clipsMode = false, onEnterClipsMode }) => {
  const params = useParams();
  const rawSlug = params.slug || '';
  const isUsernameHall = rawSlug.startsWith('@');

  const isValid = isUsernameHall
    ? USERNAME_HALL_REGEX.test(rawSlug)
    : (HALL_SLUG_REGEX.test(rawSlug) && !RESERVED_HALL_SLUGS.has(rawSlug.toLowerCase()));

  const [hall, setHall] = useState(null);
  const [loading, setLoading] = useState(isValid);
  const [notFound, setNotFound] = useState(!isValid);

  useEffect(() => {
    if (!isValid) return undefined;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetch(`/api/halls/${encodeURIComponent(rawSlug)}`, { credentials: 'include' })
      .then(async r => {
        if (r.status === 404) {
          if (!cancelled) setNotFound(true);
          return null;
        }
        if (!r.ok) throw new Error('Failed to load hall');
        return r.json();
      })
      .then(data => {
        if (!cancelled && data?.hall) setHall(data.hall);
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rawSlug, isValid]);

  if (notFound) return <NotFound />;
  if (loading || !hall) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <div className="text-xl">Loading {rawSlug}…</div>
      </div>
    );
  }

  return (
    <Home
      isAuthenticated={isAuthenticated}
      appUser={appUser}
      authReady={authReady}
      jumpDate={jumpDate}
      onJumpDateHandled={onJumpDateHandled}
      onStatusChange={onStatusChange}
      hallSlug={hall.slug}
      hallDisplayName={hall.displayName}
      clipsMode={clipsMode}
      onEnterClipsMode={onEnterClipsMode}
    />
  );
};

// Not Found component for 404 pages
const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAppMode = !location.pathname.startsWith('/legacy');

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] px-4">
      <h1 className="text-3xl font-bold mb-4">Page Not Found</h1>
      <p className="text-lg mb-6 text-center">The page you are looking for doesn't exist or has been moved.</p>
      <button
        onClick={() => navigate(isAppMode ? '/' : '/legacy')}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
      >
        Return to Home
      </button>
    </div>
  );
};

// Submit Page component (redirects to home with modal open)
const SubmitPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAppMode = !location.pathname.startsWith('/legacy');

  useEffect(() => {
    // Redirect to home page with a flag to open submit modal
    navigate(isAppMode ? '/' : '/legacy', { state: { openSubmitModal: true } });
  }, [navigate, isAppMode]);

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
      <div className="text-xl">Redirecting...</div>
    </div>
  );
};

// Main App component
function App() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Tracks whether the /api/auth/status call has completed — gates protected
  // pages (e.g. /admin/halls) from redirecting before we know who the user is.
  const [authReady, setAuthReady] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch('/api/auth/status', {
          credentials: 'include'
        });
        const data = await response.json();

        // Only consider user authenticated if they have completed account creation
        // Don't authenticate users that have only started the Steam auth flow
        if (data.isAuthenticated && !data.user?.newUser && !data.user?.pendingGoogleLink) {
          setIsAuthenticated(true);
          setUser(data.user);
          if (typeof data.user?.xp === 'number') {
            publishXp(data.user.xp);
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setAuthReady(true);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const onXp = (e) => {
      if (typeof e.detail?.xp !== 'number') return;
      setUser((prev) => (prev && prev.xp !== e.detail.xp ? { ...prev, xp: e.detail.xp } : prev));
    };
    window.addEventListener(XP_EVENT, onXp);
    return () => window.removeEventListener(XP_EVENT, onXp);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    if (typeof userData?.xp === 'number') {
      publishXp(userData.xp);
    }
    mergeGuestXpToAccount().then((xp) => {
      if (typeof xp === 'number') {
        setUser((prev) => (prev ? { ...prev, xp } : prev));
      }
    });
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
  };

  const handleSubmitVideo = () => {
    setShowSubmitModal(true);
  };

  // Check if user can submit videos (not banned)
  const canSubmitVideos = () => {
    return isAuthenticated && user && user.status !== 'Banned';
  };

  return (
    <HelmetProvider>
      <Router>
        <GoogleAnalytics />
        <AppContent
          isAuthenticated={isAuthenticated}
          user={user}
          authReady={authReady}
          showSubmitModal={showSubmitModal}
          setShowSubmitModal={setShowSubmitModal}
          handleLogin={handleLogin}
          handleLogout={handleLogout}
          handleSubmitVideo={handleSubmitVideo}
          canSubmitVideos={canSubmitVideos}
        />
      </Router>
    </HelmetProvider>
  );
}

function AppContent({
  isAuthenticated,
  user,
  authReady,
  showSubmitModal,
  setShowSubmitModal,
  handleLogin,
  handleLogout,
  handleSubmitVideo,
  canSubmitVideos
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAppMode = !location.pathname.startsWith('/legacy');
  const homePath = isAppMode ? '/' : '/legacy';
  const isHomePage = location.pathname === '/' || location.pathname === '/legacy';
  const isClipsPage = location.pathname === '/clips' || location.pathname === '/legacy/clips';
  const headerHallSlug = (() => {
    const path = location.pathname || '/';
    if (path === '/' || path === '/legacy' || path.startsWith('/legacy/')) return 'commons';
    const seg = decodeURIComponent(path.replace(/^\//, '').split('/')[0] || '');
    if (!seg || RESERVED_HALL_SLUGS.has(seg.toLowerCase())) return 'commons';
    return seg;
  })();
  const isHallFeedPage = (() => {
    const path = location.pathname || '/';
    if (path === '/' || path === '/legacy' || path.startsWith('/legacy/')) return false;
    const seg = decodeURIComponent(path.replace(/^\//, '').split('/')[0] || '');
    if (!seg || RESERVED_HALL_SLUGS.has(seg.toLowerCase())) return false;
    return HALL_SLUG_REGEX.test(seg) || USERNAME_HALL_REGEX.test(seg);
  })();
  const showStatusChip = isHomePage || isClipsPage || isHallFeedPage;

  const [statusLabel, setStatusLabel] = useState('');
  const [jumpDate, setJumpDate] = useState(null);
  const [calendarValue, setCalendarValue] = useState(() => formatLocalDate());
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [clipsMode, setClipsMode] = useState(false);

  const todayStr = formatLocalDate();

  const handleStatusChange = useCallback((next) => {
    if (!next) {
      setStatusLabel('');
      return;
    }
    if (typeof next === 'string') {
      setStatusLabel(next);
      return;
    }
    setStatusLabel(next.label || '');
    if (next.date) {
      setCalendarValue(next.date);
    }
  }, []);

  const handleDatePick = (event) => {
    const next = event.target.value;
    if (!next) return;
    setCalendarValue(next);
    setJumpDate(next);
  };

  const handleJumpDateHandled = useCallback(() => {
    setJumpDate(null);
  }, []);

  const handleToggleClips = () => {
    if (isClipsPage) {
      setClipsMode(false);
      navigate(homePath);
      return;
    }
    setClipsMode((on) => !on);
  };

  const handleEnterClipsMode = useCallback(() => {
    setClipsMode(true);
  }, []);

  const homeProps = useMemo(() => ({
    isAuthenticated,
    appUser: user,
    authReady,
    jumpDate,
    onJumpDateHandled: handleJumpDateHandled,
    onStatusChange: handleStatusChange,
    clipsMode: isClipsPage || clipsMode,
    clipsPage: isClipsPage,
    onEnterClipsMode: handleEnterClipsMode
  }), [isAuthenticated, user, authReady, jumpDate, handleJumpDateHandled, handleStatusChange, clipsMode, isClipsPage, handleEnterClipsMode]);

  return (
    <div className="app min-h-screen bg-gray-900 text-white">
      <Helmet>
        <title>BIGPLAYS.GG</title>
        <meta property="og:site_name" content="BIGPLAYS.GG" />
        <meta property="og:image" content={`${window.location.origin}/og-default.jpg`} />
      </Helmet>

      <header className="relative py-2 border-b border-gray-700">
        <div className="w-full px-4 sm:px-6">
          <div className="flex flex-row justify-between items-center gap-2 sm:gap-4">
            <h1 className="text-2xl sm:text-2xl md:text-4xl font-bold shrink-0">
              <Link
                to={homePath}
                className="hover:text-gray-300 transition-colors"
                onClick={(e) => {
                  if (window.location.pathname === '/') {
                    e.preventDefault();
                    window.location.reload();
                  }
                }}
              >
                BIGPLAYS.GG
              </Link>
            </h1>

            {/* Center: status chip (clips toggle) + calendar jump + search */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {showStatusChip && (
                <div className={`flex-shrink-0 items-center gap-1 ${isSearchExpanded ? 'hidden sm:flex' : 'flex'}`}>
                  {statusLabel ? (
                    <button
                      type="button"
                      onClick={handleToggleClips}
                      className={`px-3 py-1.5 rounded-full bg-gray-800 border text-sm font-medium whitespace-nowrap ${
                        statusLabel === 'Live'
                          ? 'border-red-700/60 text-red-500 font-semibold'
                          : statusLabel === 'Clips'
                            ? 'border-blue-600 text-blue-400'
                            : 'border-gray-700 text-white'
                      }`}
                      title={statusLabel === 'Clips' ? 'Back to videos' : 'Show clips'}
                    >
                      {statusLabel}
                    </button>
                  ) : null}
                  <label
                    className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-gray-800 border border-gray-700 text-white hover:bg-gray-700 transition-colors cursor-pointer"
                    title="Jump to a date"
                  >
                    <FaCalendarAlt className="w-3.5 h-3.5 pointer-events-none" />
                    <input
                      type="date"
                      value={calendarValue}
                      max={todayStr}
                      onChange={handleDatePick}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      aria-label="Jump to a date"
                    />
                  </label>
                </div>
              )}

              {/* Search bar — flex-1 so it fills the remaining center space on all screens */}
              <div className="flex-1 min-w-0">
                <UserSearch onExpandChange={setIsSearchExpanded} />
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4 shrink-0">
              {canSubmitVideos() && (
                <button
                  onClick={handleSubmitVideo}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 sm:p-2 flex items-center justify-center"
                  title="Submit a video"
                >
                  <FaPlus className="w-5 h-5 sm:w-5 sm:h-5" />
                </button>
              )}
              <Auth onLogin={handleLogin} onLogout={handleLogout} />
            </div>
          </div>
        </div>
      </header>

      <GuestXpPrompt isAuthenticated={isAuthenticated} authReady={authReady} />

      <Routes>
        {/* ── New version (root) ── */}
        <Route path="/" element={<Home {...homeProps} />} />
        <Route path="/clips" element={
          <ClipsPage
            jumpDate={jumpDate}
            onJumpDateHandled={handleJumpDateHandled}
            onStatusChange={handleStatusChange}
          />
        } />
        <Route path="/clip/:clipId" element={<ClipPage appUser={user} isAuthenticated={isAuthenticated} authReady={authReady} />} />
        <Route path="/user/:username" element={<UserProfile appUser={user} isAuthenticated={isAuthenticated} />} />
        <Route path="/search" element={<SearchResults isAuthenticated={isAuthenticated} />} />
        <Route path="/video/:videoId" element={<VideoPage appUser={user} isAuthenticated={isAuthenticated} authReady={authReady} />} />
        <Route path="/submit" element={
          isAuthenticated ? <SubmitPage /> : <Navigate to="/" replace />
        } />

        {/* ── Legacy version (/legacy/*) — noindexed ── */}
        <Route path="/legacy" element={<><LegacyMeta /><Home {...homeProps} /></>} />
        <Route path="/legacy/clips" element={
          <>
            <LegacyMeta />
            <ClipsPage
              jumpDate={jumpDate}
              onJumpDateHandled={handleJumpDateHandled}
              onStatusChange={handleStatusChange}
            />
          </>
        } />
        <Route path="/legacy/clip/:clipId" element={<><LegacyMeta /><ClipPage appUser={user} isAuthenticated={isAuthenticated} authReady={authReady} /></>} />
        <Route path="/legacy/user/:username" element={<><LegacyMeta /><UserProfile appUser={user} isAuthenticated={isAuthenticated} /></>} />
        <Route path="/legacy/search" element={<><LegacyMeta /><SearchResults isAuthenticated={isAuthenticated} /></>} />
        <Route path="/legacy/video/:videoId" element={<><LegacyMeta /><VideoPage appUser={user} isAuthenticated={isAuthenticated} authReady={authReady} /></>} />
        <Route path="/legacy/submit" element={
          isAuthenticated ? <><LegacyMeta /><SubmitPage /></> : <Navigate to="/legacy" replace />
        } />

        {/* ── Redirects for old /app/* bookmarks/links ── */}
        <Route path="/app" element={<Navigate to="/" replace />} />
        <Route path="/app/user/:username" element={<RedirectAppUser />} />
        <Route path="/app/video/:videoId" element={<RedirectAppVideo />} />
        <Route path="/app/search" element={<Navigate to="/search" replace />} />
        <Route path="/app/submit" element={<Navigate to="/submit" replace />} />

        <Route path="/create-account" element={<CreateAccount />} />
        <Route path="/connect-google" element={<ConnectGoogle />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/report" element={<Report />} />

        {/* ── Owner admin pages ── */}
        <Route path="/admin/halls" element={<HallsAdmin appUser={user} authReady={authReady} />} />

        {/* ── Standalone Halls ──
            Single dynamic-segment route covers both /@username and /:slug
            because React Router v6 requires a dynamic segment to occupy the
            entire path segment. HallPage detects the "@" prefix at runtime. */}
        <Route
          path="/:slug"
          element={
            <HallPage
              appUser={user}
              isAuthenticated={isAuthenticated}
              authReady={authReady}
              jumpDate={jumpDate}
              onJumpDateHandled={handleJumpDateHandled}
              onStatusChange={handleStatusChange}
              clipsMode={clipsMode}
              onEnterClipsMode={handleEnterClipsMode}
            />
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>

      <VideoSubmissionModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        isAuthenticated={isAuthenticated}
        appUser={user}
        hallSlug={headerHallSlug}
      />
    </div>
  );
}

export default App;

